import {
  Component,
  OnInit,
  ViewContainerRef,
  ViewEncapsulation,
  inject,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  HostListener,
  ChangeDetectorRef,
  effect
} from "@angular/core";
import {
  AnnotationLayerRenderedEvent,
  NgxExtendedPdfViewerModule,
  NgxExtendedPdfViewerService,
  PDFNotificationService,
  PageViewModeType,
  PagesLoadedEvent,
  PageRenderedEvent,
  TextLayerRenderedEvent
} from "ngx-extended-pdf-viewer";
import { Constants } from "@config/constants";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { UploadSignComponent } from "@components/upload-sign/upload-sign.component";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { HttpService } from "@services/http.service";
import { ActivatedRoute } from "@angular/router";
import { ToastService } from "@services/toast.service";
import { Messages } from "@config/messages";
import { MatButtonModule } from "@angular/material/button";
import { LoaderComponent } from "@components/layouts/loader/loader.component";
import { StatusMessageComponent } from "@components/layouts/status-message/status-message.component";
import FileSaver from "file-saver";
import { SignService } from "@services/sign.service";
import { IDeclineSignRequest, ISignUploadRequest } from "@modals/api.modal";
import { EnumRequestStatus, EnumSignerRoleType, EnumSignTagTypes } from "@modals/app.modal";
import { MatCardModule } from "@angular/material/card";
import { AppService } from "@services/app.service";
import { MatMenuModule } from "@angular/material/menu";
import { HeaderBeforeLoginService } from "@services/services/header-before-login.service";
import { Subscription, debounceTime, fromEvent } from "rxjs";
import { DeclineSignRequestConfirmationComponent } from "@components/decline-sign-request-confirmation/decline-sign-request-confirmation.component";
import { ErsdAcceptanceComponent } from "@components/ersd-acceptance/ersd-acceptance.component";
import { LanguageService, SupportedLanguage } from "@services/language.service";
import { TranslateService } from "@ngx-translate/core";
import { SignToolbarService, SignToolbarAction, SignToolbarListItem } from "@services/sign-toolbar.service";
import { FIELD_DETECTION_KEYWORDS } from "@config/field-detection-keywords.constant";
import type { FieldTypeKey } from "@config/field-detection-keywords.constant";
import { PdfFieldDetectionService } from "@services/pdf-field-detection.service";
import type { IPdfFieldSuggestion, IPdfInsights, IPdfStats, IPdfPageText } from "@services/pdf-field-detection.service";
import { FormsModule } from "@angular/forms";

type KapybaraChatRole = 'user' | 'assistant';
interface IKapybaraChatMessage {
  id: string;
  role: KapybaraChatRole;
  text: string;
  at: number;
  isTyping?: boolean;
}

@Component({
  selector: "kzn-sign",
  standalone: true,
  imports: [
    NgxExtendedPdfViewerModule,
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LoaderComponent,
    StatusMessageComponent,
    MatCardModule,
    MatMenuModule,
    FormsModule,
  ],
  providers: [HttpService],
  templateUrl: "./sign.component.html",
  styleUrl: "./sign.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class SignComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('additionalInfoSection') additionalInfoSection!: ElementRef;
  @ViewChild('mobileToolbarScroller') mobileToolbarScroller?: ElementRef<HTMLElement>;
  @ViewChild('responseBlock') responseBlock?: ElementRef<HTMLElement>;
  @ViewChild('kapybaraChatMessagesContainer') kapybaraChatMessagesEl?: ElementRef<HTMLElement>;
  @ViewChild('kapybaraSidebarScroll') kapybaraSidebarScrollEl?: ElementRef<HTMLElement>;
  
  base64!: string;
  isDownloading: boolean = true;
  isSubmitted: boolean = false;
  isDeclined: boolean = false;
  isSigned: boolean = false;
  isError: boolean = false;
  showAdditionalInfo: boolean = false;
  isMobileView: boolean = false;
  isCompactView: boolean = false;
  isMobileToolbarExpanded: boolean = false;
  fileName: string = "";
  isView: boolean = false;
  selectedLanguage: SupportedLanguage = 'en';
  statusMessage!: {
    heading: string;
    message: string;
    isSuccess: boolean;
  };
  pageViewMode: PageViewModeType = "infinite-scroll";
  signerRole!: EnumSignerRoleType;
  signerName: string = '';
  EnumSignerRoleType = EnumSignerRoleType;
  EnumSignTagTypes = EnumSignTagTypes;
  isERSDAccepted: boolean = false;
  
 
  hasSignHereElements: boolean = false;
  pdfReady: boolean = false;

  isPdfFullyScrolled: boolean = false;
  drawerOpenedByUser: boolean = false;
  /** Latch: end-of-document UI only after PDF canvas has painted + short settle delay */
  private _endPanelDelayPassed = false;
  /** True once the user has scrolled the document (avoids auto-end on short PDFs) */
  private _userHasScrolledDocument = false;
  kapybaraSidebarOpen: boolean = false;
  private _kapybaraSidebarOpenedBefore: boolean = false;
  fieldDetectionKeywords = FIELD_DETECTION_KEYWORDS;
  pdfFieldSuggestions: IPdfFieldSuggestion[] = [];
  pdfSuggestionsLoading: boolean = false;
  pdfInsights: IPdfInsights | null = null;
  pdfStats: IPdfStats | null = null;
  pdfPageTexts: IPdfPageText[] = [];
  pdfInsightsLoading: boolean = false;
  pdfInsightsError: boolean = false;
  private _pdfInsightsLoaded: boolean = false;
  private _pdfPagesLoaded: boolean = false;
  showPdfSuggestionsSection: boolean = false;
  showDocInfoSection: boolean = false;
  showPdfInsightsSection: boolean = false;
  assistantInputText: string = '';
  kapybaraChatInput: string = '';
  kapybaraChatMessages: IKapybaraChatMessage[] = [];
  showHelpMenu: boolean = false;
  assistantResponseText: string = '';
  assistantResponseShimmer: boolean = false;
  suggestedQuestions: Array<{ text: string; icon: string; action?: string; primary?: boolean }> = [];
  private _pdfLoaded: boolean = false;
  private _overflowFixSeq = 0;
  private _scrollSubscription: Subscription = new Subscription();
  private _pagesCount!: number;
  private _pageNumber!: number;
  private _fileRequestID: string = "";
  private _signerID: string = "";
  private _selectedSpan: HTMLElement | undefined;
  private _signatureTag: string = "";
  private _order: string = "";
  private _signatureDynamicTag: string = "";
  private _requiredSignatureHereCount: any = {};
  
  private _countedSignHereTagIds: Set<string> = new Set();

  private _submitMissingSignaturePages: number[] = [];
  
  private _signSetupDone: boolean = false;
  private _signChromeReady: boolean = false;

  private _signPlacementTimer: ReturnType<typeof setTimeout> | null = null;
  private _signPagePlacementTimers = new Map<number, ReturnType<typeof setTimeout>>();
  private _signTagWatchSeq = 0;
  private _signTagWatchTimers: ReturnType<typeof setTimeout>[] = [];
  private _signTargetPrimeIndex = 0;

  private _zoomBurstTimers: ReturnType<typeof setTimeout>[] = [];
  private _tagLayoutRestoreTimer: ReturnType<typeof setTimeout> | null = null;
  private _tagLayoutRestoreInFlight = false;
  private _tagLayoutRestoreQueued = false;
 
  private _signLayoutObserver: MutationObserver | null = null;
  
  private _fitZoomFactor: number | null = null;
  private _zoomClampScheduled = false;
  
  private _zoomFloorWidth = 0;
 
  private _signNextBtnAttached: boolean = false;
  /** While a tag is being dragged/dropped, keep Next invisible and non-clickable. */
  tagDragHidesNext = false;
  private _nextRestoreAfterTagDragTimer: ReturnType<typeof setTimeout> | null = null;
  
  private _signHereDetectedEver: boolean = false;
  
  private _signTargets: Array<{ page: number; y: number; kind: 'A' | 'B'; signTagsIndex?: number; occurrenceInPage?: number; }> = [];
  
  private _signTargetCursor: number = -1;
  
  private _signTargetsBuilding: boolean = false;
 
  private _nextHighlightSeq: number = 0;
  private _pdfService = inject(NgxExtendedPdfViewerService);
  private _pdfNotification = inject(PDFNotificationService);
  private _httpService = inject(HttpService);
  private _activatedRoute = inject(ActivatedRoute);
  private _toastService = inject(ToastService);
  private _headerBeforeLoginService = inject(HeaderBeforeLoginService);
  private _cdr = inject(ChangeDetectorRef);
  private _languageService = inject(LanguageService);
  private _translate = inject(TranslateService);
  private _signToolbarService = inject(SignToolbarService);
  private _pdfFieldDetection = inject(PdfFieldDetectionService);
  private _langSubscription: Subscription = new Subscription();
  private _translateSubscription: Subscription = new Subscription();
  private _toolbarSubscription: Subscription = new Subscription();

  
  private _pinchZoomGuardAttached = false;
  private _isInsidePdfViewer(target: EventTarget | null): boolean {
    const el = target as HTMLElement | null;
    return !!el?.closest?.(
      'ngx-extended-pdf-viewer, #viewerContainer, .pdfViewer, #outerContainer',
    );
  }
  
  private _isZoomBlockingOverlayOpen(): boolean {
    return !!document.querySelector(
      '.cdk-overlay-container .mat-mdc-dialog-container,' +
        '.cdk-overlay-container .cdk-dialog-container,' +
        '.cdk-overlay-container .mat-mdc-dialog-surface',
    );
  }
  private _blockZoomEvent(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    (event as unknown as { stopImmediatePropagation?: () => void })
      .stopImmediatePropagation?.();
  }
 
  private readonly _onGestureZoom = (event: Event): void => {
    if (this._isZoomBlockingOverlayOpen()) {
      this._blockZoomEvent(event);
      return;
    }
    if (this._isInsidePdfViewer(event.target)) return; 
    event.preventDefault();
  };
  
  private readonly _onMultiTouchZoom = (event: TouchEvent): void => {
    if (!event.touches || event.touches.length <= 1) return;
    if (this._isZoomBlockingOverlayOpen()) {
      this._blockZoomEvent(event);
      return;
    }
    if (this._isInsidePdfViewer(event.target)) return;
    event.preventDefault();
  };
  /** Block ctrl/⌘ + wheel zoom (trackpad pinch). */
  private readonly _onWheelZoom = (event: WheelEvent): void => {
    if (!(event.ctrlKey || event.metaKey)) return;
    if (this._isZoomBlockingOverlayOpen()) {
      this._blockZoomEvent(event);
      return;
    }
    if (this._isInsidePdfViewer(event.target)) return;
    event.preventDefault();
  };

  kapybaraRevealed = this._headerBeforeLoginService.kapybaraRevealed;
  

  private _ERSDDialogData: { 
    requesterName: string; 
    customerName: string; 
    requesterMessage: string; 
    EULAMessage: string; 
    openDeclineConfirmationDialog: () => void 
  } = Object.create({});
  private _ersdDialogRef: MatDialogRef<ErsdAcceptanceComponent> | null = null;

  get documentRequesterName(): string {
    return this._ERSDDialogData?.requesterName || '';
  }
  get documentRequesterMessage(): string {
    return this._ERSDDialogData?.requesterMessage || '';
  }
  get documentCustomerName(): string {
    return this._ERSDDialogData?.customerName || '';
  }

  t(key: string, params?: Record<string, string | number>): string {
    const p = params ? Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)])) : undefined;
    return this._languageService.instant(key, p);
  }
  private isHindiText(text: string): boolean {
    if (/[\u0900-\u097F]/.test(text)) return true;
    const lower = text.toLowerCase();
    const hindiWords = [
      'kya', 'hai', 'kaise', 'kahan', 'kaun', 'kab', 'kyun', 'kyu',
      'batao', 'bataye', 'bataiye', 'samjhao', 'samjhaye',
      'mujhe', 'mereko', 'humko', 'hume',
      'karo', 'kardo', 'krdo', 'karein',
      'chahiye', 'chahte', 'chaiye',
      'dekho', 'dekhao', 'dikhao',
      'namaste', 'namaskar', 'dhanyavad', 'shukriya', 'alvida',
    ];
    const words = lower.split(/\s+/);
    let hindiCount = 0;
    for (const w of words) {
      if (hindiWords.includes(w)) hindiCount++;
    }
    return hindiCount >= 2 || (words.length <= 3 && hindiCount >= 1 && !/\b(hi|hey|hello)\b/.test(lower));
  }

  private resp(en: string, _hi?: string, _q?: string): string {
    return en;
  }

  onKapybaraHeaderClick1(): void {
    this.kapybaraRevealed.set(false);
    // this.kapybaraOpenSidebarRequest.set(true);
  }

  get isReviewer(): boolean {
    return (this.signerRole || '').toLowerCase() === EnumSignerRoleType.REVIEWER.toLowerCase();
  }

  get signToolbarItems() {
    const items = [
      { key: 'signature', kind: 'signature', labelKey: 'signature', icon: 'add' },
      { key: 'comment', kind: 'comment', labelKey: 'comment', icon: 'add_comment' },
      ...this.orderedSignTags.map((tag) => ({
        key: String(tag.type),
        kind: 'tag',
        labelKey: tag.type.toLowerCase(),
        icon: tag.icon,
        type: tag.type,
        dimensions: tag.dimensions,
      })),
    ].filter((item) =>
      this._signToolbarService.isToolbarItemVisible(item as SignToolbarListItem)
    );
    return items as Array<{
      key: string;
      kind: 'signature' | 'comment' | 'tag';
      labelKey: string;
      icon: string;
      type?: EnumSignTagTypes;
      dimensions?: any;
    }>;
  }

  get visibleToolbarItems() {
    const items = this.signToolbarItems;
    if (!this.isMobileView || this.isMobileToolbarExpanded) return items;
    return items.slice(0, 5);
  }

  get hiddenToolbarItemsCount(): number {
    if (!this.isMobileView || this.isMobileToolbarExpanded) return 0;
    return Math.max(0, this.signToolbarItems.length - 5);
  }

  get showMobileMoreToggle(): boolean {
    return this.isMobileView && (this.isMobileToolbarExpanded || this.hiddenToolbarItemsCount > 0);
  }

  get pdfLoaded(): boolean {
    return this._pdfLoaded;
  }

  get isSinglePage(): boolean {
    return this._pagesCount === 1;
  }

  get showDrawerPanel(): boolean {
    if (!this._pdfLoaded || !this.pdfReady || !this._endPanelDelayPassed) return false;
    if (this.isSinglePage && this.isMobileView) return this.drawerOpenedByUser;
    return this.isPdfFullyScrolled && this.showAdditionalInfo;
  }

  get endPanelDelayPassed(): boolean {
    return this._endPanelDelayPassed;
  }

  openDrawerByClick(): void {
    this.drawerOpenedByUser = true;
    this._cdr.detectChanges();
  }

  toggleKapybaraSidebar(): void {
    this.kapybaraSidebarOpen = !this.kapybaraSidebarOpen;
    if (this.kapybaraSidebarOpen) {
      this.updateKapybaraSuggestedQuestions();
      this.loadPdfSuggestions();
      this.loadPdfInsights();
      this.ensureKapybaraChatSeeded();
      if (!this._kapybaraSidebarOpenedBefore) {
        this._kapybaraSidebarOpenedBefore = true;
        this.scrollKapybaraSidebarToTop();
      }
    }
    this._cdr.detectChanges();
  }

  closeKapybaraSidebar(): void {
    this.kapybaraSidebarOpen = false;
    this._cdr.detectChanges();
  }

  private loadPdfSuggestions(): void {
    if (!this.base64 || this.isView || this.pdfSuggestionsLoading) return;
    if (this.pdfFieldSuggestions.length > 0) return;
    this.pdfSuggestionsLoading = true;
    this._cdr.detectChanges();
    this._pdfFieldDetection.scanPdfForSuggestions(this.base64).then((suggestions) => {
      this.pdfFieldSuggestions = suggestions;
      this.pdfSuggestionsLoading = false;
      this._cdr.detectChanges();
    }).catch(() => {
      this.pdfSuggestionsLoading = false;
      this._cdr.detectChanges();
    });
  }

  private loadPdfInsights(): void {
    if (this._pdfInsightsLoaded) return;
    if (!this.base64 || this.isView) return;
    this.pdfInsightsLoading = true;
    this.pdfInsightsError = false;
    this._cdr.detectChanges();
    Promise.all([
      this._pdfFieldDetection.extractPdfInsights(this.base64, { maxPages: 5, maxChars: 6000 }),
      this._pdfFieldDetection.getPdfStats(this.base64),
      this._pdfFieldDetection.extractAllPagesText(this.base64),
    ])
      .then(([insights, stats, pageTexts]) => {
        this.pdfInsights = insights;
        this.pdfStats = stats ?? null;
        this.pdfPageTexts = pageTexts;
        this._pdfPagesLoaded = pageTexts.length > 0;
        this.pdfInsightsLoading = false;
        this._pdfInsightsLoaded = true;
        this._cdr.detectChanges();
      })
      .catch(() => {
        this.pdfInsightsLoading = false;
        this.pdfInsightsError = true;
        this._pdfInsightsLoaded = true;
        this._cdr.detectChanges();
      });
  }

  private ensureKapybaraChatSeeded(): void {
    if (this.kapybaraChatMessages.length > 0) return;
    this.pushKapybaraChatMessage(
      'assistant',
      `Ask me anything about signing, uploading, or this document. I can answer app “how-to” questions and also use readable PDF text when available.`
    );
  }

  sendKapybaraChat(): void {
    const q = (this.kapybaraChatInput || '').trim();
    if (!q) return;
    this.pushKapybaraChatMessage('user', q);
    this.kapybaraChatInput = '';

    if (this.isHindiText(q)) {
      this.pushKapybaraChatMessage('assistant', 'English support only. Please type your question in English.');
      return;
    }

    const quick = this.tryHandleChatQuickAction(q);
    if (quick) {
      this.pushKapybaraChatMessage('assistant', quick);
      return;
    }

    const typingId = this.pushKapybaraTyping();
    setTimeout(() => {
      const answer = this.answerFromPdf(q);
      this.replaceKapybaraTyping(typingId, answer);
    }, 450);
  }

  private pushKapybaraTyping(): string {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const msg: IKapybaraChatMessage = { id, role: 'assistant', text: '', at: Date.now(), isTyping: true };
    this.kapybaraChatMessages = [...this.kapybaraChatMessages, msg].slice(-50);
    this._cdr.detectChanges();
    this.scrollKapybaraChatToBottom();
    return id;
  }

  private replaceKapybaraTyping(id: string, text: string): void {
    const next = this.kapybaraChatMessages.map((m) => {
      if (m.id !== id) return m;
      return { ...m, isTyping: false, text, at: Date.now() };
    });
    this.kapybaraChatMessages = next.slice(-50);
    this._cdr.detectChanges();
    this.scrollKapybaraChatToBottom();
  }

  private tryHandleChatQuickAction(message: string): string | null {
    const q = this.normalizeForMatch(message);
    if (!q) return null;
    if (q.includes('start tour') || q.includes('tour')) {
      this.handleAssistantAction('startTour');
      return `Starting the tour.`;
    }
    if (q.includes('quick tips') || q.includes('tips') || q.includes('tip')) {
      this.handleAssistantAction('showTips');
      return `Here are some quick tips.`;
    }
    return null;
  }

  private pushKapybaraChatMessage(role: KapybaraChatRole, text: string): void {
    const msg: IKapybaraChatMessage = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      role,
      text,
      at: Date.now(),
    };
    this.kapybaraChatMessages = [...this.kapybaraChatMessages, msg].slice(-50);
    this._cdr.detectChanges();
    this.scrollKapybaraChatToBottom();
  }

  private scrollKapybaraSidebarToTop(): void {
    const el = this.kapybaraSidebarScrollEl?.nativeElement;
    if (!el) return;
    setTimeout(() => {
      try {
        el.scrollTop = 0;
      } catch {
       
      }
    }, 50);
  }

  private scrollKapybaraChatToBottom(): void {
    const el = this.kapybaraChatMessagesEl?.nativeElement;
    if (!el) return;
    setTimeout(() => {
      try {
        const scrollContainer = el.closest('.kapybara-sidebar__scroll') as HTMLElement | null;
        if (scrollContainer) {
          scrollContainer.scrollTop = scrollContainer.scrollHeight;
        } else {
          el.scrollTop = el.scrollHeight;
        }
      } catch {
        // ignore
      }
    }, 30);
  }

  private answerFromPdf(questionRaw: string): string {
    const question = (questionRaw || '').trim();
    const q = question.toLowerCase();

    const signWhereResult = this.answerWhereToSign(question);
    if (signWhereResult) return signWhereResult;

    const appHelp = this.answerAppHelp(question);
    if (appHelp) return appHelp;

    if (this.pdfInsightsLoading && !this._pdfInsightsLoaded) {
      return this.resp("I'm still reading the PDF text. Please try again in a moment.", "PDF text abhi read ho raha hai. Thodi der baad phir try karein.", question);
    }

    if (this.pdfInsightsError) {
      return this.resp("I couldn't read text from this PDF, so I can't answer questions from it.", "Is PDF se text read nahi ho paya, isliye main iske baare me jawab nahi de sakta.", question);
    }

    const preview = (this.pdfInsights?.previewText || '').trim();

    
    if (this.isPdfWordCountQuestion(question)) {
      const words = this.pdfStats?.totalWords ?? preview.split(/\s+/).filter(Boolean).length;
      return this.resp(`This PDF has about ${words.toLocaleString()} words.`, `Is PDF me lagbhag ${words.toLocaleString()} shabd hain.`, question);
    }
    if (this.isPdfCharCountQuestion(question)) {
      const chars = this.pdfStats?.totalChars ?? preview.length;
      return this.resp(`This PDF has about ${chars.toLocaleString()} characters of readable text.`, `Is PDF me lagbhag ${chars.toLocaleString()} characters hain.`, question);
    }
    if (this.isPdfSizeQuestion(question)) {
      const bytes = this.pdfStats?.sizeBytes;
      if (!bytes) return this.resp("I couldn't get the file size.", "File size nahi mil paya.", question);
      const kb = (bytes / 1024).toFixed(1);
      const mb = (bytes / (1024 * 1024)).toFixed(2);
      const sizeStr = bytes >= 1024 * 1024 ? mb + ' MB' : kb + ' KB';
      return this.resp(
        `This PDF is about ${sizeStr} (${bytes.toLocaleString()} bytes).`,
        `Is PDF ka size lagbhag ${sizeStr} hai (${bytes.toLocaleString()} bytes).`,
        question
      );
    }
    if (this.isPdfColorQuestion(question)) {
      return this.resp("I can't detect color mode (B&W vs color) from the text I read. Color is set when the PDF is created.", "Text se color mode (B&W ya color) detect nahi kar sakta. Color PDF banate waqt set hota hai.", question);
    }

    if (!preview && this.pdfPageTexts.length === 0) {
      const isAboutQ = this.isPdfAboutQuestion(question);
      if (isAboutQ) {
        return this.resp(
          "I couldn't extract readable text from this PDF (it may be a scanned image or image-only). I can still help with: signing, adding Signature/Date/Text fields, or how to use the toolbar.",
          undefined,
          question
        );
      }
      return this.resp("I couldn't find readable text in this PDF. Try asking about fields to add (Signature/Date/Name), or open a different PDF.", undefined, question);
    }

    const fullText = this.pdfPageTexts.map(p => p.text).join('\n') || preview;

   
    const pageSearchResult = this.answerPageQuestion(question);
    if (pageSearchResult) return pageSearchResult;

    
    const searchResult = this.answerWordSearch(question);
    if (searchResult) return searchResult;

    
    const pageContentResult = this.answerPageContentQuestion(question);
    if (pageContentResult) return pageContentResult;

    if (this.isPdfAboutQuestion(question)) {
      return this.resp(this.describePdfTopic(fullText, false), undefined, question);
    }

    if (this.isPdfToughPointQuestion(question)) {
      return this.resp(this.describePdfTopic(fullText, false), undefined, question);
    }

    if (this.isPdfClarifyConceptQuestion(question)) {
      const { en } = this.getPdfOverviewBilingual(fullText);
      return this.resp(en + "\n\nAsk me about any term above and I'll explain it in simpler words.", undefined, question);
    }

    if (this.isPdfOverviewQuestion(question)) {
      const { en } = this.getPdfOverviewBilingual(fullText);
      return this.resp(en, undefined, question);
    }

    if (this.isPdfGapsQuestion(question)) {
      const { en } = this.getPdfGapsBilingual(fullText);
      return this.resp(en, undefined, question);
    }

    if (this.isPdfThemesQuestion(question)) {
      const { en } = this.getPdfThemesBilingual(fullText);
      return this.resp(en, undefined, question);
    }

    // Page count question
    if (/how many page|how much page|total page|number of page|page count|pages?\s+in\s+(?:this\s+)?pdf|how many pages?/i.test(q)) {
      const count = this.pdfPageTexts.length || this.pdfInsights?.numPages || 0;
      return this.resp(count ? `This PDF has ${count} page${count > 1 ? 's' : ''}.` : "I couldn't determine the page count.", undefined, question);
    }

    if (q.includes('sent by') || q.includes('sender') || q.includes('requester')) {
      const by = this.documentRequesterName || this.documentCustomerName;
      if (!by) return this.resp("I don't see Sent by details for this document.", undefined, question);
      const suffix = this.documentCustomerName ? ` (${this.documentCustomerName})` : '';
      const val = `${this.documentRequesterName}${suffix}`.trim();
      return this.resp(`Sent by: ${val}`, undefined, question);
    }
    if (q.includes('signer') || q.includes('who is signing') || q.includes('who will sign')) {
      const val = this.signerName ? this.signerName : '';
      return this.resp(val ? `Signer: ${val}` : "I don't see signer name details here.", undefined, question);
    }

    const entities = this.pdfInsights?.entities;
    if (q.includes('email')) {
      const emails = entities?.emails ?? [];
      const list = emails.join(', ');
      return this.resp(emails.length ? `Emails found in PDF: ${list}` : "I couldn't find any email addresses in the readable PDF text.", undefined, question);
    }
    if (q.includes('phone') || q.includes('mobile') || q.includes('contact')) {
      const phones = entities?.phones ?? [];
      const list = phones.join(', ');
      return this.resp(phones.length ? `Phone numbers found in PDF: ${list}` : "I couldn't find any phone numbers in the readable PDF text.", undefined, question);
    }
    if (/\bdate\b/.test(q) || q.includes('dated')) {
      const dates = entities?.dates ?? [];
      const list = dates.join(', ');
      return this.resp(dates.length ? `Dates found in PDF: ${list}` : "I couldn't find any date patterns in the readable PDF text.", undefined, question);
    }
    if (q.includes('amount') || q.includes('total') || q.includes('price') || q.includes('₹') || q.includes('$') || q.includes('inr') || q.includes('usd')) {
      const amounts = entities?.amounts ?? [];
      const list = amounts.join(', ');
      return this.resp(amounts.length ? `Amounts found in PDF: ${list}` : "I couldn't find any amounts in the readable PDF text.", undefined, question);
    }

    // Smart search across all pages
    const allPagesSearch = this.searchAcrossPages(question);
    if (allPagesSearch) return allPagesSearch;

    const best = this.findBestPdfSnippet(question, fullText);
    if (best && best.score >= 2) {
      const snippet = best.text.length > 280 ? best.text.slice(0, 280).trimEnd() + '…' : best.text;
      return this.resp(`In the PDF, I found this related text:\n"${snippet}"`, undefined, question);
    }

    // Final fallback: try matching anything from user's question in PDF pages
    const fallbackSearch = this.fallbackPdfSearch(question);
    if (fallbackSearch) return fallbackSearch;

    return this.resp(
      "I couldn't find a direct match in the PDF for your question. Try asking:\n• \"What is this PDF about?\"\n• \"Search <word>\" to find a specific word\n• \"Which page has <topic>?\"\n• Emails, dates, amounts in the PDF\n• How to sign, add fields, or use the toolbar",
      undefined,
      question
    );
  }

  private isPdfWordCountQuestion(questionRaw: string): boolean {
    const q = this.normalizeForMatch(questionRaw);
    if (!q) return false;
    return (
      q.includes('word count') ||
      q.includes('how many word') ||
      q.includes('total word')
    );
  }

  private isPdfCharCountQuestion(questionRaw: string): boolean {
    const q = this.normalizeForMatch(questionRaw);
    if (!q) return false;
    return (
      q.includes('character count') ||
      q.includes('how many character') ||
      q.includes('total character')
    );
  }

  private isPdfSizeQuestion(questionRaw: string): boolean {
    const q = this.normalizeForMatch(questionRaw);
    if (!q) return false;
    return (
      q.includes('pdf size') ||
      q.includes('file size') ||
      q.includes('document size')
    );
  }

  private isPdfColorQuestion(questionRaw: string): boolean {
    const q = this.normalizeForMatch(questionRaw);
    if (!q) return false;
    return (
      q.includes('pdf color') ||
      q.includes('pdf colour') ||
      q.includes('document color') ||
      q.includes('bw') ||
      q.includes('black white') ||
      q.includes('color mode')
    );
  }

  private isPdfToughPointQuestion(questionRaw: string): boolean {
    const q = this.normalizeForMatch(questionRaw);
    if (!q) return false;
    return (
      q.includes('tough point') ||
      q.includes('key point') ||
      q.includes('important point') ||
      q.includes('main point') ||
      q.includes('highlight') ||
      q.includes('pdf tough') ||
      q.includes('pdf key')
    );
  }

  private isPdfClarifyConceptQuestion(questionRaw: string): boolean {
    const q = this.normalizeForMatch(questionRaw);
    if (!q) return false;
    return (
      q.includes('clarify') ||
      q.includes('complex concept') ||
      q.includes('explain') ||
      q.includes('what is this pdf about') ||
      q.includes('simple words')
    );
  }

  private isPdfOverviewQuestion(questionRaw: string): boolean {
    const q = this.normalizeForMatch(questionRaw);
    if (!q) return false;
    return (
      q.includes('overview') ||
      q.includes('findings') ||
      q.includes('draft overview') ||
      q.includes('executive brief') ||
      q.includes('summary') ||
      q.includes('brief') ||
      q.includes('short summary')
    );
  }

  private isPdfGapsQuestion(questionRaw: string): boolean {
    const q = this.normalizeForMatch(questionRaw);
    if (!q) return false;
    return (
      q.includes('gaps') ||
      q.includes('missing') ||
      q.includes('identify gap') ||
      q.includes('potential gap') ||
      q.includes('lack') ||
      q.includes('incomplete')
    );
  }

  private isPdfThemesQuestion(questionRaw: string): boolean {
    const q = this.normalizeForMatch(questionRaw);
    if (!q) return false;
    return (
      q.includes('themes') ||
      q.includes('key themes') ||
      q.includes('main themes') ||
      q.includes('summarize theme') ||
      q.includes('topics') ||
      q.includes('main topics')
    );
  }

  private isPdfAboutQuestion(questionRaw: string): boolean {
    const q = this.normalizeForMatch(questionRaw);
    if (!q) return false;
    return (
      q.includes('what is this pdf about') ||
      q.includes('what is this document about') ||
      q.includes('what is the pdf about') ||
      q.includes('what is the document about') ||
      q.includes('what is this about') ||
      q.includes('about this pdf') ||
      q.includes('about this document') ||
      q.includes('pdf about') ||
      q.includes('document about') ||
      q.includes('related') ||
      q.includes('is pdf about') ||
      q.includes('this pdf about')
    );
  }

  private describePdfTopic(previewText: string, inHindi: boolean = false): string {
    const preview = (previewText || '').trim();
    if (!preview) return "I couldn't find readable text in this PDF.";

    const docType = this.guessPdfDocType(preview);
    const headline = this.pickPdfHeadline(preview);
    const keywords = this.extractTopKeywords(preview, 7);
    const entities = this.pdfInsights?.entities;

    const parts: string[] = [];
    parts.push(docType ? `This PDF looks like a ${docType}.` : `Here’s what this PDF seems related to (based on readable text):`);

    if (headline) parts.push(`\nFirst line(s): “${headline}”`);
    if (keywords.length) parts.push(`\nKey topics: ${keywords.join(', ')}`);

    const entityBits: string[] = [];
    if ((entities?.amounts?.length ?? 0) > 0) entityBits.push(`amounts: ${entities!.amounts.slice(0, 3).join(', ')}`);
    if ((entities?.dates?.length ?? 0) > 0) entityBits.push(`dates: ${entities!.dates.slice(0, 3).join(', ')}`);
    if ((entities?.emails?.length ?? 0) > 0) entityBits.push(`emails: ${entities!.emails.slice(0, 2).join(', ')}`);
    if ((entities?.phones?.length ?? 0) > 0) entityBits.push(`phones: ${entities!.phones.slice(0, 2).join(', ')}`);
    if (entityBits.length) parts.push(`\nI also found ${entityBits.join(' | ')}.`);

    if (this.pdfPageTexts.length > 0) {
      parts.push(`\nTotal pages: ${this.pdfPageTexts.length}`);
    }
    parts.push(`\nYou can ask: "search <word>", "which page has <term>?", "show page 2", or "what does it say about <keyword>?"`);
    return parts.join('\n');
  }

  private getPdfOverviewBilingual(previewText: string): { en: string; hi: string } {
    const preview = (previewText || '').trim();
    if (!preview) {
      return { en: "I couldn't find readable text in this PDF.", hi: "I couldn't find readable text in this PDF." };
    }
    const docType = this.guessPdfDocType(preview);
    const headline = this.pickPdfHeadline(preview);
    const keywords = this.extractTopKeywords(preview, 7);
    const entities = this.pdfInsights?.entities;

    const entityBits: string[] = [];
    if ((entities?.amounts?.length ?? 0) > 0) entityBits.push(`amounts: ${entities!.amounts.slice(0, 3).join(', ')}`);
    if ((entities?.dates?.length ?? 0) > 0) entityBits.push(`dates: ${entities!.dates.slice(0, 3).join(', ')}`);
    if ((entities?.emails?.length ?? 0) > 0) entityBits.push(`emails: ${entities!.emails.slice(0, 2).join(', ')}`);
    if ((entities?.phones?.length ?? 0) > 0) entityBits.push(`phones: ${entities!.phones.slice(0, 2).join(', ')}`);

    const en = [
      docType ? `This PDF looks like a ${docType}.` : "Here's what this PDF seems related to:",
      headline ? `\nFirst line(s): "${headline}"` : '',
      keywords.length ? `\nKey topics: ${keywords.join(', ')}` : '',
      entityBits.length ? `\nFound: ${entityBits.join(' | ')}.` : '',
    ].join('');

    return { en: en || "No summary available.", hi: en || "No summary available." };
  }

  private getPdfGapsBilingual(previewText: string): { en: string; hi: string } {
    const entities = this.pdfInsights?.entities;
    const docType = this.guessPdfDocType((previewText || '').trim());
    const gaps: string[] = [];

    if (!(entities?.dates?.length ?? 0)) gaps.push('dates');
    if (!(entities?.emails?.length ?? 0)) gaps.push('email addresses');
    if (!(entities?.phones?.length ?? 0)) gaps.push('phone numbers');
    if (!(entities?.amounts?.length ?? 0)) gaps.push('amounts/figures');

    if (docType && (docType.includes('Invoice') || docType.includes('Contract')) && !(entities?.amounts?.length ?? 0)) {
      gaps.push('financial figures (expected in this document type)');
    }

    const en = gaps.length
      ? `Potential gaps in the content: ${gaps.join(', ')}. These may be in images or non-extractable parts of the PDF.`
      : "I found dates, emails, phones, and amounts in the text. No obvious gaps—but some info might be in images or tables I can't read.";

    return { en, hi: en };
  }

  private getPdfThemesBilingual(previewText: string): { en: string; hi: string } {
    const preview = (previewText || '').trim();
    if (!preview) {
      return { en: "I couldn't find readable text.", hi: "I couldn't find readable text." };
    }
    const keywords = this.extractTopKeywords(preview, 8);
    const docType = this.guessPdfDocType(preview);

    const en = keywords.length
      ? `Key themes: ${keywords.join(', ')}.${docType ? ` Document type: ${docType}.` : ''}`
      : "I couldn't extract key themes from the text.";

    return { en, hi: en };
  }

  private guessPdfDocType(previewText: string): string | null {
    const t = this.normalizeForMatch(previewText);
    if (!t) return null;
    const rules: Array<{ type: string; any: string[] }> = [
      { type: 'Invoice', any: ['invoice', 'tax invoice', 'bill to', 'amount due', 'gst', 'vat'] },
      { type: 'Agreement / Contract', any: ['agreement', 'contract', 'terms and conditions', 'party', 'parties', 'whereas'] },
      { type: 'Offer / Proposal', any: ['proposal', 'quotation', 'quote', 'estimate', 'scope of work', 'offer'] },
      { type: 'Purchase Order', any: ['purchase order', 'po number', 'order number'] },
      { type: 'NDA', any: ['non disclosure', 'confidentiality', 'nda'] },
      { type: 'Authorization / Consent', any: ['authorize', 'authorisation', 'consent', 'permission'] },
    ];
    for (const r of rules) {
      if (r.any.some((k) => t.includes(this.normalizeForMatch(k)))) return r.type;
    }
    return null;
  }

  private pickPdfHeadline(previewText: string): string {
    const lines = (previewText || '').split(/\n+/).map((s) => s.trim()).filter(Boolean);
    const pick = lines.slice(0, 3).filter((l) => l.length >= 8).slice(0, 2).join(' — ');
    return pick.length > 140 ? pick.slice(0, 140).trimEnd() + '…' : pick;
  }

  private extractTopKeywords(previewText: string, cap: number): string[] {
    const tokens = this.tokenizeForMatch(previewText);
    const counts = new Map<string, number>();
    for (const t of tokens) {
      if (t.length < 3) continue;
      if (/^\d+$/.test(t)) continue;
      counts.set(t, (counts.get(t) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, cap)
      .map(([k]) => k);
  }

  private answerAppHelp(questionRaw: string): string | null {
    const q = this.normalizeForMatch(questionRaw);
    if (!q) return null;

    const hasAny = (words: string[]) => words.some((w) => q.includes(w));
    
    const hasGreeting = () => /\b(hi|hello|hey)\b/.test(q) || /^(good morning|good afternoon|good evening|good night)/.test(q);

    if (hasGreeting()) {
      return this.resp("Hi! I'm here to help with signing, uploading, and this document. Ask me anything.", undefined, questionRaw);
    }

    if (hasAny(['thanks', 'thank you'])) {
      return this.resp("You're welcome! If you need more help, just ask.", undefined, questionRaw);
    }

    if (hasAny(['bye', 'goodbye', 'see you'])) {
      return this.resp("Bye! Good luck with your document.", undefined, questionRaw);
    }

    if (hasAny(['how are you'])) {
      return this.resp("I'm good, thanks! How can I help you with this document today?", undefined, questionRaw);
    }

    if (hasAny(['who are you', 'what can you do', 'help', 'what do you do'])) {
      return this.resp("I'm your document assistant. I can help with: Signing, Upload (max 200 MB), PDF info (emails, dates, amounts), Toolbar (Stamp, Comment, etc). Just ask!", undefined, questionRaw);
    }

    if (hasAny(['toolbar', 'tool bar', 'top bar', 'tools', 'tool', 'menu', 'options'])) {
      return this.resp("Toolbar: Select Signature, Initials, Text, Date, Checkbox, Stamp. Click or drag on PDF to place. Use Next to jump fields, then Submit.", undefined, questionRaw);
    }

    if (hasAny(['stamp', 'seal', 'stempel', 'sello'])) {
      return this.resp("Stamp: Choose Stamp from toolbar, click/drag on PDF, upload stamp image (PNG/JPG), adjust size, then Submit.", undefined, questionRaw);
    }

    if (hasAny(['reset signature', 'change signature', 'new signature', 'replace signature', 'update signature'])) {
      return this.resp("To reset your signature: Click on the existing signature on the PDF, delete it (trash icon or delete key), then add a new one from the toolbar (Signature → draw/type/upload).", undefined, questionRaw);
    }

    if (hasAny(['signature', 'esign'])) {
      return this.resp("Signature: Click Signature in toolbar, draw/type/upload your signature, place on PDF, move/resize, then Submit.", undefined, questionRaw);
    }

    if (hasAny(['comment', 'comment box'])) {
      return this.resp("Comment: Add a note on the PDF. Click Comment, click on PDF, type your note. Others can see and reply.", undefined, questionRaw);
    }

    if (hasAny(['initials', 'initial', 'short sign'])) {
      return this.resp("Initials: Short form of your name (e.g. JD). Click Initials, draw/type, place on PDF, then Submit.", undefined, questionRaw);
    }

    if (hasAny(['text', 'text field', 'type text', 'type'])) {
      return this.resp("Text: Type free-form text (name, address). Click Text, click/drag on PDF, enter text, then Submit.", undefined, questionRaw);
    }

    if (hasAny(['date', 'date field'])) {
      return this.resp("Date: Pick or enter a date. Click Date, click/drag on PDF, select from picker or type, then Submit.", undefined, questionRaw);
    }

    if (hasAny(['checkbox', 'check box', 'tick'])) {
      return this.resp("Checkbox: Tick/check for yes/no. Click Checkbox, place on PDF, tick/untick as needed, then Submit.", undefined, questionRaw);
    }

    if (hasAny(['register', 'signup', 'sign up', 'create account', 'new account', 'account create', 'registration'])) {
      return this.resp("No public Register screen. Signers: open the signing link, no account needed. For login: ask admin for credentials.", undefined, questionRaw);
    }

    if (hasAny(['login', 'log in', 'signin', 'sign in', 'otp', 'reset password', 'forgot', 'password'])) {
      return this.resp("Login: Go to Login screen, sign in. Forgot password? Use Reset Password. OTP: you'll be routed after login.", undefined, questionRaw);
    }

    if (hasAny(['max size', 'maximum size', 'upload limit', 'allowed size', 'file limit', 'upload size', 'maximum file', 'mb', 'megabyte']) &&
        !hasAny(['is pdf', 'this pdf', 'current document', 'document size'])) {
      const maxMb = Math.round(Constants.MAX_SIGN_UPLOAD_FILE_SIZE_IN_KB / 1024);
      return this.resp(`You can upload a PDF up to ${maxMb} MB. Format: PDF only. Larger file? Try compressing or splitting.`, undefined, questionRaw);
    }

    if (hasAny(['upload', 'pdf upload', 'upload pdf', 'create request', 'new request', 'send document', 'manage users', 'add users'])) {
      return this.resp("Upload PDF: Login, open New Request, upload PDF, Manage Users to add recipients, add Signature/Date fields, Submit.", undefined, questionRaw);
    }

    if (hasAny(['sign', 'signature', 'initial', 'initials', 'submit', 'complete', 'finish', 'signed'])) {
      return this.resp("Sign: Open signing link, use toolbar to add Signature/fields or click highlighted fields, use Next, then Submit.", undefined, questionRaw);
    }

    if (hasAny(['next', 'next field', 'next sign'])) {
      return this.resp("Click Next → click on the Sign Here image on the PDF → Add your signature (draw/type/upload) → click Add → then Next for the next field. Repeat until done, then Submit.", undefined, questionRaw);
    }

    if (hasAny(['decline', 'reject', 'cancel', 'deny'])) {
      return this.resp("To decline: click Decline, enter reason if asked, confirm. Sender will be notified.", undefined, questionRaw);
    }

    if (hasAny(['tag', 'field', 'add field', 'add tag', 'signature field', 'date field', 'text field', 'drag', 'drop'])) {
      return this.resp("Add fields: use top toolbar (Signature/Date/Name), drag & drop the tag onto the right place in the PDF.", undefined, questionRaw);
    }

    return null;
  }

  private findBestPdfSnippet(question: string, pdfText: string): { text: string; score: number } | null {
    const qTokens = this.tokenizeForMatch(question);
    if (qTokens.length === 0) return null;

    const chunks = this.splitPdfIntoChunks(pdfText);
    let bestText = '';
    let bestScore = 0;

    for (const chunk of chunks) {
      const normChunk = this.normalizeForMatch(chunk);
      if (!normChunk) continue;
      let score = 0;
      for (const t of qTokens) {
        if (t.length < 3) continue;
        if (normChunk.includes(t)) score++;
      }
      if (score > bestScore) {
        bestScore = score;
        bestText = chunk;
      }
    }

    return bestScore > 0 ? { text: bestText, score: bestScore } : null;
  }

  /** Answer "on which page do I need to sign?", "where to add signature?" using PDF suggestions. */
  private answerWhereToSign(questionRaw: string): string | null {
    const q = questionRaw.toLowerCase();
    const isSignQuestion =
      /(?:on\s+)?which\s+page\s+(?:do\s+i\s+)?(?:need\s+to\s+)?sign/i.test(q) ||
      /(?:on\s+)?which\s+page\s+(?:i\s+)?(?:need\s+to\s+)?add\s+(?:signature|sign)/i.test(q) ||
      /where\s+(?:to|should\s+i|do\s+i)\s+sign/i.test(q) ||
      /where\s+(?:we\s+)?(?:need\s+to\s+|to\s+)?add\s+(?:signature|sign)/i.test(q) ||
      /(?:which|what)\s+page\s+(?:for|to)\s+sign/i.test(q) ||
      (/\bsign\b/.test(q) && (/\bpage\b/.test(q) || /\bwhere\b/.test(q)));
    if (!isSignQuestion) return null;

    const suggestions = this.pdfGroupedSuggestions;
    if (!suggestions.length) {
      return this.resp(
        "I didn't find specific sign spots in this PDF. You can add your signature anywhere using the toolbar: pick Signature, then click on the PDF where you want it. Common places: end of document, near your name, or next to date fields.",
        undefined,
        questionRaw
      );
    }

    const parts: string[] = ['You can add signature/fields on these pages. Click on the PDF near the text shown:'];
    for (const g of suggestions.slice(0, 5)) {
      const line = (g.matchedText || '').slice(0, 55).trim();
      const suffix = (g.matchedText || '').length > 55 ? '…' : '';
      const fieldLabel = this.formatFieldTypesLabel(g.fieldTypes);
      parts.push(`• Page ${g.page}: Add ${fieldLabel} — near "${line}${suffix}"`);
    }
    return this.resp(parts.join('\n'), undefined, questionRaw);
  }

  /** Answer "which page has X", "where is X", "on which page X" */
  private answerPageQuestion(questionRaw: string): string | null {
    const q = questionRaw.toLowerCase();
    const pagePatterns = [
      /(?:which|what)\s+page\s+(?:has|contains?|mentions?|shows?|includes?)\s+(.+)/i,
      /(?:where|on which page)\s+(?:is|are|does|do|can i find|to find)\s+(.+)/i,
      /(?:page|pages?)\s+(?:for|of|with|about)\s+(.+)/i,
      /(?:find|locate|search)\s+(.+?)\s+(?:page|in pdf|in document)/i,
      /(?:where)\s+(?:to|should i|do i)\s+(?:sign|put|place|add)\s+(.+)/i,
    ];

    let searchTerm: string | null = null;
    for (const p of pagePatterns) {
      const m = q.match(p);
      if (m?.[1]) { searchTerm = m[1].trim(); break; }
    }
    if (!searchTerm || searchTerm.length < 2) return null;

    const cleaned = searchTerm.replace(/[?!.'"]/g, '').trim();
    if (!cleaned) return null;

    const results = this._pdfFieldDetection.searchInPages(this.pdfPageTexts, cleaned);
    if (results.length === 0) {
      return this.resp(`I couldn't find "${cleaned}" on any page of this PDF.`, undefined, questionRaw);
    }

    const parts: string[] = [`Found "${cleaned}" on ${results.length} page${results.length > 1 ? 's' : ''}:`];
    for (const r of results.slice(0, 5)) {
      const line = r.lines[0] ? ` — "${r.lines[0].trim().slice(0, 100)}"` : '';
      parts.push(`• Page ${r.page}${line}`);
    }
    if (results.length > 5) parts.push(`…and ${results.length - 5} more page(s).`);
    return this.resp(parts.join('\n'), undefined, questionRaw);
  }

  /** Answer "what is on page 3", "show page 2 content" */
  private answerPageContentQuestion(questionRaw: string): string | null {
    const m = questionRaw.match(/(?:page|pg)\s*(\d+)/i);
    if (!m) return null;
    const pageNum = parseInt(m[1], 10);
    if (!pageNum || pageNum < 1) return null;

    const hasPageKeyword = /(?:what|show|read|content|text|display|tell)\b/i.test(questionRaw) ||
                           /\bpage\s*\d+\s*(?:say|content|text|about)?$/i.test(questionRaw);
    if (!hasPageKeyword) return null;

    const pageData = this.pdfPageTexts.find(p => p.page === pageNum);
    if (!pageData) {
      const totalPages = this.pdfPageTexts.length || this.pdfInsights?.numPages || 0;
      return this.resp(`Page ${pageNum} doesn't exist. This PDF has ${totalPages} page${totalPages > 1 ? 's' : ''}.`, undefined, questionRaw);
    }

    const text = pageData.text.trim();
    if (!text) return this.resp(`Page ${pageNum} doesn't have any readable text (it might be an image or blank page).`, undefined, questionRaw);

    const snippet = text.length > 400 ? text.slice(0, 400).trimEnd() + '…' : text;
    return this.resp(`Page ${pageNum} content:\n\n"${snippet}"`, undefined, questionRaw);
  }

  /** Answer "search <word>", "find <word>" */
  private answerWordSearch(questionRaw: string): string | null {
    const q = questionRaw.toLowerCase().trim();
    const searchPatterns = [
      /^(?:search|find|look for|lookup|look up)\s+[""']?(.+?)[""']?\s*$/i,
      /^(?:search|find)\s+(?:the\s+)?(?:word|text|phrase|term)\s+[""']?(.+?)[""']?\s*$/i,
      /^(?:is|does)\s+(?:the\s+)?(?:word|text|phrase)\s+[""']?(.+?)[""']?\s+(?:in|present|exist|there|found)/i,
    ];

    let searchTerm: string | null = null;
    for (const p of searchPatterns) {
      const m = q.match(p);
      if (m?.[1]) { searchTerm = m[1].trim(); break; }
    }
    if (!searchTerm || searchTerm.length < 2) return null;

    const cleaned = searchTerm.replace(/[?!.'"]/g, '').trim();
    if (!cleaned) return null;

    const results = this._pdfFieldDetection.searchInPages(this.pdfPageTexts, cleaned);
    if (results.length === 0) {
      return this.resp(`The word/phrase "${cleaned}" was not found in this PDF.`, undefined, questionRaw);
    }

    const parts: string[] = [`"${cleaned}" found on ${results.length} page${results.length > 1 ? 's' : ''}:`];
    for (const r of results.slice(0, 5)) {
      const line = r.lines[0] ? ` — "${r.lines[0].trim().slice(0, 120)}"` : '';
      parts.push(`• Page ${r.page}${line}`);
    }
    return this.resp(parts.join('\n'), undefined, questionRaw);
  }

  /** Search question keywords across all pages for a best-match answer. */
  private searchAcrossPages(questionRaw: string): string | null {
    if (this.pdfPageTexts.length === 0) return null;

    const tokens = this.tokenizeForMatch(questionRaw);
    if (tokens.length === 0) return null;

    let bestPage = 0;
    let bestScore = 0;
    let bestLine = '';

    for (const p of this.pdfPageTexts) {
      const norm = p.text.toLowerCase();
      let score = 0;
      for (const t of tokens) {
        if (t.length < 3) continue;
        if (norm.includes(t)) score++;
      }
      if (score > bestScore) {
        bestScore = score;
        bestPage = p.page;
        const lines = p.text.split(/\n/).filter(Boolean);
        for (const line of lines) {
          const ll = line.toLowerCase();
          let lineScore = 0;
          for (const t of tokens) {
            if (t.length >= 3 && ll.includes(t)) lineScore++;
          }
          if (lineScore >= Math.ceil(tokens.length * 0.4)) {
            bestLine = line.trim().slice(0, 200);
            break;
          }
        }
      }
    }

    if (bestScore >= 2 && bestPage > 0) {
      const parts: string[] = [`Found related content on Page ${bestPage}:`];
      if (bestLine) parts.push(`"${bestLine}"`);
      parts.push(`\nTip: Ask "search <word>" to find any word, or "which page has <term>" for location.`);
      return this.resp(parts.join('\n'), undefined, questionRaw);
    }
    return null;
  }

  /** Last-resort: take significant words from user question, search in PDF pages. */
  private fallbackPdfSearch(questionRaw: string): string | null {
    if (this.pdfPageTexts.length === 0) return null;

    const tokens = this.tokenizeForMatch(questionRaw).filter(t => t.length >= 4);
    if (tokens.length === 0) return null;

    for (const token of tokens) {
      const results = this._pdfFieldDetection.searchInPages(this.pdfPageTexts, token);
      if (results.length > 0) {
        const r = results[0];
        const line = r.lines[0] ? `"${r.lines[0].trim().slice(0, 150)}"` : '';
        return this.resp(
          `I found "${token}" on Page ${r.page}${line ? ':\n' + line : '.'}\n\nFor a better answer, try asking more specifically or use "search <word>".`,
          undefined,
          questionRaw
        );
      }
    }
    return null;
  }

  private splitPdfIntoChunks(text: string): string[] {
    const lines = (text || '').split(/\n+/).map((s) => s.trim()).filter(Boolean);
    const out: string[] = [];
    for (const line of lines) {
      const parts = line.split(/[.?!]\s+/).map((s) => s.trim()).filter(Boolean);
      for (const p of parts) {
        if (p.length >= 20) out.push(p);
      }
    }
    return out.length ? out : lines;
  }

  private normalizeForMatch(text: string): string {
    const lower = (text || '').toLowerCase();
   
    try {
      const re = new RegExp('[^\\p{L}\\p{N}\\s]', 'gu');
      return lower.replace(re, ' ').replace(/\s+/g, ' ').trim();
    } catch {
      return lower.replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim();
    }
  }

  private tokenizeForMatch(text: string): string[] {
    const stop = new Set([
      'the','a','an','is','are','am','was','were','to','of','and','or','in','on','for','with','from','as','at','by',
      'please','can','you','me','i','we','it','this','that','these','those','what','which','who','whom','when','where','why','how',
      'about','does','do','has','have','had','its','my','your','our','their','they','he','she','not','no','yes',
      'tell','show','find','search','page','pages',
      'pdf','document','doc'
    ]);
    const norm = this.normalizeForMatch(text);
    const tokens = norm.split(' ').map((s) => s.trim()).filter(Boolean);
    const filtered = tokens.filter((t) => !stop.has(t));
    return Array.from(new Set(filtered));
  }

  getSuggestionFieldLabel(fieldType: FieldTypeKey): string {
    const cat = FIELD_DETECTION_KEYWORDS.find((c) => c.fieldType === fieldType);
    return cat ? this.t(cat.labelKey) : fieldType;
  }

  getSuggestionFieldIcon(fieldType: FieldTypeKey): string {
    const cat = FIELD_DETECTION_KEYWORDS.find((c) => c.fieldType === fieldType);
    return cat?.icon ?? 'edit';
  }

  get pdfGroupedSuggestions(): Array<{ page: number; matchedText: string; fieldTypes: FieldTypeKey[] }> {
    return this._pdfFieldDetection.groupSuggestions(this.pdfFieldSuggestions, 8);
  }

  formatFieldTypesLabel(fieldTypes: FieldTypeKey[]): string {
    return fieldTypes.map((ft) => this.getSuggestionFieldLabel(ft)).join(', ');
  }

  private updateKapybaraSuggestedQuestions(): void {
    const total = this.signService.allTagsCount;
    const completed = this.signService.getCompletedFieldsCount(this._countedSignHereTagIds);
    if (completed >= total && total > 0) {
      this.suggestedQuestions = [
        { text: this.t('assistantQSubmitNow'), icon: 'send', action: 'submit', primary: true },
        { text: this.t('assistantQReviewAgain'), icon: 'visibility', action: 'review' },
        { text: this.t('assistantQFeedback'), icon: 'feedback', action: 'feedback' },
      ];
    } else {
      this.suggestedQuestions = [
        { text: this.t('assistantQProgress'), icon: 'show_chart', action: 'showProgress' },
        { text: this.t('assistantQRemaining'), icon: 'hourglass_empty', action: 'remainingFields' },
        { text: this.t('assistantQTips'), icon: 'tips_and_updates', action: 'showTips' },
      ];
    }
  }

  showAssistantResponse(text: string): void {
    this.assistantResponseText = '';
    this.assistantResponseShimmer = true;
    this._cdr.detectChanges();
    setTimeout(() => {
      this.assistantResponseText = text;
      this.assistantResponseShimmer = false;
      this._cdr.detectChanges();
      setTimeout(() => {
        this.responseBlock?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 50);
    }, 400);
  }

  handleAssistantAction(action: string): void {
    switch (action) {
      case 'submit':
        this.signPDF();
        break;
      case 'review':
        this.isPdfFullyScrolled = true;
        this.showAdditionalInfo = true;
        this._cdr.detectChanges();
        break;
      case 'feedback':
        this.shareFeedback();
        break;
      case 'showProgress':
      case 'remainingFields':
        this.showFieldSummary();
        break;
      case 'showTips':
        this.showAssistantResponse(this.t('assistantQuickTipsList').replace(/\*\*/g, ''));
        break;
      case 'startTour':
        this.showAssistantResponse(this.t('tourStep1'));
        break;
      case 'fieldDetectionFlow':
        this.showAssistantResponse(this.getFieldDetectionLogicFlowText());
        break;
    }
    this._cdr.detectChanges();
  }

  private getFieldDetectionLogicFlowText(): string {
    return [
      'FIELD DETECTION LOGIC FLOW (Keyword + Context + Confidence)',
      '',
      'Overview:',
      'When a PDF is uploaded → extract text + positions → detect keywords → score confidence → suggest or auto-place fields.',
      '',
      '1) PDF Text Extraction',
      '- Extract text blocks with page + x,y + font size + line breaks',
      '- Detect blank lines / underscore lines',
      '',
      '2) Normalize Text',
      '- lowercase, trim, remove punctuation, normalize spaces',
      '',
      '3) Keyword Match Engine',
      '- Exact match (+50)',
      '- Partial match (+25)',
      '- Optional fuzzy match (e.g., edit distance) (+20)',
      '',
      '4) Context Analysis',
      '- Blank line/whitespace near keyword (+20)',
      '- Sentence context (e.g., "sign and date") (+30) → suggest Signature + Date',
      '- Page position (bottom 30% likely signature) (+10)',
      '- Duplicate penalty if already detected (-20)',
      '',
      '5) Confidence Score',
      'Confidence = match + context + position - penalties',
      '',
      '6) Decision Thresholds',
      '70+ → Auto-place',
      '40–69 → Suggest',
      '<40 → Ignore',
      '',
      '7) Field Positioning',
      '- If blank line exists: align field to blank/underscore',
      '- Else: place ~20px below keyword or right-aligned if label is right',
      '',
      '8) Field Type Mapping',
      'signature → Signature field',
      'date → Date picker',
      'email → Email',
      'phone → Phone',
      'address → Multi-line text',
      'agree/consent → Checkbox',
      '',
      '9) Multi-signer Intelligence (optional)',
      'Detect roles like Applicant/Witness/Representative and pre-assign fields.',
      '',
      '10) User Confirmation Layer',
      'Highlight detected fields → user can approve/delete/reassign/move.',
      '',
      'Advanced (future):',
      '- Trie indexing, caching patterns/templates, ML/AI context improvements, multi-language keywords.',
    ].join('\n');
  }

  showFieldSummary(): void {
    const total = this.signService.allTagsCount;
    const completed = this.signService.getCompletedFieldsCount(this._countedSignHereTagIds);
    const msg = total > 0
      ? this.t('assistantProgressDetailed', { completed, total, remaining: total - completed, time: Math.ceil((total - completed) * 0.5) })
      : this.t('assistantNoFields');
    this._toastService.showInfo(msg);
  }

  shareFeedback(): void {
    this._toastService.showInfo(this.t('assistantFeedbackQuestion'));
  }

  sendAssistantMessage(): void {
    const text = this.assistantInputText?.trim();
    if (!text) return;
    this.assistantInputText = '';
    const lower = text.toLowerCase();
    if (lower.includes('sign') || lower.includes('how')) {
      this._toastService.showInfo(this.t('assistantHowToSignDetailed').replace(/\n/g, ' '));
    } else if (lower.includes('progress') || lower.includes('remaining')) {
      this.showFieldSummary();
    } else if (lower.includes('submit')) {
      this.handleAssistantAction('submit');
    } else {
      this._toastService.showInfo(this.t('assistantDefaultResponse'));
    }
    this._cdr.detectChanges();
  }

  get orderedSignTags() {
    const order = [
      EnumSignTagTypes.INITIALS,
      EnumSignTagTypes.TEXT,
      EnumSignTagTypes.DATE,
      EnumSignTagTypes.CHECKBOX,
      EnumSignTagTypes.STAMP
    ];
    const tagMap = new Map(
      (this.signService.customSignTags || []).map((tag) => [tag.type, tag])
    );
    return order
      .map((type) => tagMap.get(type))
      .filter((tag): tag is NonNullable<typeof tag> => Boolean(tag));
  }

  scrollToolbar(direction: 'left' | 'right'): void {
    const scroller = this.mobileToolbarScroller?.nativeElement;
    if (!scroller) return;
    const amount = Math.max(120, Math.round(scroller.clientWidth * 0.6));
    scroller.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  }

  toggleMobileToolbar(): void {
    this.isMobileToolbarExpanded = !this.isMobileToolbarExpanded;
  }

  onToolbarItemClick(item: { kind: 'signature' | 'comment' | 'tag'; type?: EnumSignTagTypes; dimensions?: any }): void {
    if (item.kind === 'signature') {
      this.openSignDialog(false, true);
      return;
    }
    if (item.kind === 'comment') {
      this.openSignDialog(true, true);
      return;
    }
    if (item.type) {
      this.activateClickMode(item.type, item.dimensions);
    }
  }

  onToolbarItemDragStart(event: DragEvent, item: { kind: 'signature' | 'comment' | 'tag'; type?: EnumSignTagTypes; dimensions?: any }): void {
    if (!event.dataTransfer) return;
    if (this.isToolbarItemDisabled(item)) return;
    if (item.kind === 'tag') {
      if (!item.type) return;
      const payload = JSON.stringify({ type: item.type, dimensions: item.dimensions });
      event.dataTransfer.setData('application/kzn-tag', payload);
      event.dataTransfer.effectAllowed = 'copy';
    } else if (item.kind === 'signature' || item.kind === 'comment') {
      const payload = JSON.stringify({ kind: item.kind });
      event.dataTransfer.setData('application/kzn-sign-placeholder', payload);
      event.dataTransfer.effectAllowed = 'copy';
    } else {
      return;
    }
    this._setNextButtonSuppressedForTagDrag(true);
    const clear = () => {
      window.removeEventListener('dragend', clear);
      this._setNextButtonSuppressedForTagDrag(false, 120);
    };
    window.addEventListener('dragend', clear);
  }

  onPdfDragOver(event: DragEvent): void {
    if (!event.dataTransfer) return;
    const types = Array.from(event.dataTransfer.types || []);
    if (
      types.includes('application/kzn-tag') ||
      types.includes('application/kzn-signature') ||
      types.includes('application/kzn-sign-placeholder')
    ) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  onPdfDrop(event: DragEvent): void {
    if (!event.dataTransfer) return;
    const placeholderData = event.dataTransfer.getData('application/kzn-sign-placeholder');
    if (placeholderData) {
      event.preventDefault();
      let parsed: { kind?: 'signature' | 'comment' } = {};
      try {
        parsed = JSON.parse(placeholderData);
      } catch {
        return;
      }
      if (!parsed.kind) return;
      this.createSignPlaceholderDrop(event, parsed.kind);
      return;
    }
    const signatureData = event.dataTransfer.getData('application/kzn-signature');
    if (signatureData) {
      event.preventDefault();
      let parsed: {
        imageBase64?: string;
        signatureId?: string;
        isCommentOnly?: boolean;
        signComment?: string;
        comment?: string;
        isResponsiveContainer?: boolean;
      } = {};
      try {
        parsed = JSON.parse(signatureData);
      } catch {
        return;
      }
      if (!parsed.imageBase64) return;
      this.handleSignatureDrop(event, { ...parsed, imageBase64: parsed.imageBase64! });
      return;
    }
    const data = event.dataTransfer.getData('application/kzn-tag');
    if (!data) return;
    event.preventDefault();

    let parsed: { type?: EnumSignTagTypes; dimensions?: any } = {};
    try {
      parsed = JSON.parse(data);
    } catch {
      return;
    }
    if (!parsed.type) return;

    const elements = document.elementsFromPoint(event.clientX, event.clientY) as HTMLElement[];
    const textLayer = elements.find((el) => el.classList?.contains('textLayer')) as HTMLElement | undefined;
    if (!textLayer) return;

    this.generateSignTag({ clientX: event.clientX, clientY: event.clientY } as MouseEvent, textLayer, parsed.type, parsed.dimensions);
  }

  private createSignPlaceholderDrop(event: DragEvent, kind: 'signature' | 'comment'): void {
    this.createSignPlaceholderDropAtPoint(event.clientX, event.clientY, kind);
  }

  private createSignPlaceholderDropAtPoint(clientX: number, clientY: number, kind: 'signature' | 'comment'): void {
    const elements = document.elementsFromPoint(clientX, clientY) as HTMLElement[];
    const textLayer = elements.find((el) => el.classList?.contains('textLayer')) as HTMLElement | undefined;
    if (!textLayer) return;
    const pageEl = textLayer.closest('.page') as HTMLElement | null;
    const pages = Array.from(document.getElementsByClassName('page')) as HTMLElement[];
    const pageIndex = pageEl ? pages.indexOf(pageEl) : 0;
    const rect = textLayer.getBoundingClientRect();
    const leftPercent = ((clientX - rect.left) / rect.width) * 100;
    const topPercent = ((clientY - rect.top) / rect.height) * 100;

    const element = document.createElement('span');
    element.style.position = 'absolute';
    element.style.left = `${leftPercent}%`;
    element.style.top = `${topPercent}%`;
    element.setAttribute('page', pageIndex.toString());
    element.setAttribute('data-drag-placeholder', 'true');
    element.setAttribute('data-kzn-tag-id', `dp-${pageIndex}-${Date.now().toString(36)}`);
    
    element.setAttribute('data-kzn-signhere-bound', 'true');
    if (kind === 'comment') {
      element.className = 'box-sign-here sign-tag-comment-container comment-placeholder-container';
    } else {
      element.classList.add('sign-here-standard');
      const isMobileSign = typeof window !== 'undefined' && window.innerWidth <= 768;
      element.style.width = isMobileSign
        ? Constants.MOBILE_SIGN_IMAGE_WIDTH
        : Constants.DEFAULT_SIGN_IMAGE_WIDTH;
      if (isMobileSign) {
        element.style.maxWidth = Constants.MOBILE_SIGN_IMAGE_MAX_WIDTH;
        element.style.minWidth = Constants.MOBILE_SIGN_IMAGE_MIN_WIDTH;
      }
    }
    textLayer.appendChild(element);

    if (kind === 'comment') {
      element.innerHTML = '';
      const wrapper = document.createElement('div');
      wrapper.className = 'comment-placeholder';
      const img = document.createElement('img');
      img.setAttribute('src', 'assets/comment-icon.png');
      img.className = 'img-comment-here';
      const label = document.createElement('span');
      label.className = 'comment-placeholder-text';
      label.textContent = this.t('comment') || 'Comment';
      wrapper.appendChild(img);
      wrapper.appendChild(label);
      element.appendChild(wrapper);
      const dragBtn = document.createElement('button');
      dragBtn.type = 'button';
      dragBtn.className = 'placeholder-drag-btn material-icons';
      dragBtn.innerText = 'open_with';
      dragBtn.addEventListener('click', (e) => e.stopPropagation());
      this.attachPlaceholderDrag(dragBtn, element, textLayer);
      element.appendChild(dragBtn);
      const deleteBtn = document.createElement('button');
      deleteBtn.type = 'button';
      deleteBtn.className = 'placeholder-delete-btn material-icons';
      deleteBtn.innerText = 'delete';
      deleteBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        element.remove();
      });
      element.appendChild(deleteBtn);
      element.addEventListener('click', (ev: Event) => {
        if (element.dataset['dragging'] === '1') {
          element.dataset['dragging'] = '0';
          return;
        }
        const target = ev.target as HTMLElement | null;
        if (target?.closest('.resizer, .action-handles-container')) return;
        this._selectedSpan = element;
        this._pageNumber = pageIndex;
        this.openSignDialog(true, false, element.classList.contains('sign-tag-comment-container'));
      });
      return;
    }

    const signElement = this.signService.renderSignHereElement(element);
    const dragBtn = document.createElement('button');
    dragBtn.type = 'button';
    dragBtn.className = 'placeholder-drag-btn material-icons';
    dragBtn.innerText = 'open_with';
    dragBtn.addEventListener('click', (e) => e.stopPropagation());
    this.attachPlaceholderDrag(dragBtn, element, textLayer);
    element.appendChild(dragBtn);
    const deleteBtn = document.createElement('button');
    deleteBtn.type = 'button';
    deleteBtn.className = 'placeholder-delete-btn material-icons';
    deleteBtn.innerText = 'delete';
    deleteBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      element.remove();
    });
    element.appendChild(deleteBtn);
    signElement.addEventListener('click', (ev: Event) => {
      if (element.dataset['dragging'] === '1') {
        element.dataset['dragging'] = '0';
        return;
      }
      const target = ev.target as HTMLElement | null;
      if (target?.closest('.resizer, .action-handles-container')) return;
      this._selectedSpan = element;
      this._pageNumber = pageIndex;
      this.openSignDialog(false, false, true);
    });
  }

  /**
   * Hide Next and block clicks while a tag is dragged; restore shortly after drop
   * so pointerup cannot land on Next.
   */
  private _setNextButtonSuppressedForTagDrag(suppressed: boolean, restoreDelayMs = 0): void {
    if (this._nextRestoreAfterTagDragTimer) {
      clearTimeout(this._nextRestoreAfterTagDragTimer);
      this._nextRestoreAfterTagDragTimer = null;
    }

    const apply = (hide: boolean) => {
      this.tagDragHidesNext = hide;
      this._cdr.markForCheck();
      const nextEl = document.getElementById('btn__next') as HTMLElement | null;
      if (!nextEl) {
        if (!hide) this._updateNextButton();
        return;
      }
      if (hide) {
        nextEl.classList.remove('show');
        nextEl.style.display = 'none';
        nextEl.style.visibility = 'hidden';
        nextEl.style.pointerEvents = 'none';
        nextEl.setAttribute('aria-hidden', 'true');
        nextEl.setAttribute('tabindex', '-1');
        return;
      }
      nextEl.style.removeProperty('display');
      nextEl.style.removeProperty('visibility');
      nextEl.style.removeProperty('pointer-events');
      nextEl.removeAttribute('aria-hidden');
      nextEl.removeAttribute('tabindex');
      this._updateNextButton();
    };

    if (suppressed) {
      apply(true);
      return;
    }
    if (restoreDelayMs > 0) {
      this._nextRestoreAfterTagDragTimer = setTimeout(() => {
        this._nextRestoreAfterTagDragTimer = null;
        apply(false);
      }, restoreDelayMs);
      return;
    }
    apply(false);
  }

  private attachPlaceholderDrag(handle: HTMLElement, element: HTMLElement, textLayer: HTMLElement): void {
    const startDrag = (
      startX: number,
      startY: number,
      onMoveEvent: (cb: (x: number, y: number) => void) => () => void,
      onUpEvent: (cb: () => void) => () => void
    ) => {
      let moved = false;
      let rafId: number | null = null;
      let lastX = startX;
      let lastY = startY;

      // Suppress Next as soon as the drag handle is pressed (before move threshold).
      this._setNextButtonSuppressedForTagDrag(true);

      const update = (x: number, y: number) => {
        lastX = x;
        lastY = y;
        if (rafId !== null) return;
        rafId = window.requestAnimationFrame(() => {
          const rect = textLayer.getBoundingClientRect();
          const dx = Math.abs(lastX - startX);
          const dy = Math.abs(lastY - startY);
          if (dx + dy > 2) {
            moved = true;
            element.dataset['dragging'] = '1';
            this._setNextButtonSuppressedForTagDrag(true);
          }
          const leftPercent = ((lastX - rect.left) / rect.width) * 100;
          const topPercent = ((lastY - rect.top) / rect.height) * 100;
          element.style.left = `${leftPercent}%`;
          element.style.top = `${topPercent}%`;
          rafId = null;
        });
      };

      const removeMove = onMoveEvent(update);
      const removeUp = onUpEvent(() => {
        removeMove();
        removeUp();
        if (rafId !== null) {
          window.cancelAnimationFrame(rafId);
          rafId = null;
        }
        if (!moved) element.dataset['dragging'] = '0';
        // Keep Next hidden through drop / pointerup so it cannot receive the click.
        this._setNextButtonSuppressedForTagDrag(false, 180);
      });
    };

    const onPointerDown = (event: PointerEvent) => {
      event.preventDefault();
      event.stopPropagation();
      startDrag(
        event.clientX,
        event.clientY,
        (update) => {
          const move = (e: PointerEvent) => update(e.clientX, e.clientY);
          window.addEventListener('pointermove', move, { passive: false });
          return () => window.removeEventListener('pointermove', move);
        },
        (done) => {
          window.addEventListener('pointerup', done, { passive: false });
          return () => window.removeEventListener('pointerup', done);
        }
      );
    };

    const onTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) return;
      event.preventDefault();
      event.stopPropagation();
      startDrag(
        touch.clientX,
        touch.clientY,
        (update) => {
          const move = (e: TouchEvent) => {
            const t = e.touches[0];
            if (t) update(t.clientX, t.clientY);
          };
          window.addEventListener('touchmove', move, { passive: false });
          return () => window.removeEventListener('touchmove', move);
        },
        (done) => {
          window.addEventListener('touchend', done, { passive: false });
          window.addEventListener('touchcancel', done, { passive: false });
          return () => {
            window.removeEventListener('touchend', done);
            window.removeEventListener('touchcancel', done);
          };
        }
      );
    };

    handle.addEventListener('pointerdown', onPointerDown);
    handle.addEventListener('touchstart', onTouchStart, { passive: false });
  }

  private handleSignatureDrop(
    event: DragEvent,
    payload: {
      imageBase64: string;
      signatureId?: string;
      isCommentOnly?: boolean;
      signComment?: string;
      comment?: string;
      isResponsiveContainer?: boolean;
    }
  ): void {
    const elements = document.elementsFromPoint(event.clientX, event.clientY) as HTMLElement[];
    const page = elements.find((el) => el.classList?.contains('page')) as HTMLElement | undefined;
    if (!page) return;
    const textLayer = page.querySelector('.textLayer') as HTMLElement | null;
    if (!textLayer) return;
    const pages = Array.from(document.getElementsByClassName('page')) as HTMLElement[];
    const pageIndex = pages.indexOf(page);
    if (pageIndex < 0) return;
    const rect = textLayer.getBoundingClientRect();
    const left = `${event.clientX - rect.left}px`;
    const top = `${event.clientY - rect.top}px`;
    this.signService.addSignImage(
      payload.imageBase64,
      left,
      top,
      pageIndex,
      this._viewContainerRef,
      true,
      payload.isCommentOnly ?? false,
      payload.signComment,
      undefined,
      payload.comment,
      payload.signatureId,
      payload.isResponsiveContainer
    );
  }

  private _touchDragItem?: { type?: EnumSignTagTypes; dimensions?: any };
  private _touchDragKind?: 'tag' | 'signature' | 'comment';
  private _touchDragGhost?: HTMLElement;
  private _prevBodyTouchAction?: string;
  private _ghostRafId: number | null = null;
  private _ghostLastX: number = 0;
  private _ghostLastY: number = 0;
  private _touchMoveListener?: (e: TouchEvent) => void;
  private _touchEndListener?: (e: TouchEvent) => void;
  private _pointerMoveListener?: (e: PointerEvent) => void;
  private _pointerUpListener?: (e: PointerEvent) => void;
  private _didDropThisDrag: boolean = false;
  private _dragStartPoint?: { x: number; y: number };
  private _dragActive: boolean = false;
  private _suppressPointerFromTouch: boolean = false;

  onToolbarItemTouchStart(event: TouchEvent, item: { kind: 'signature' | 'comment' | 'tag'; type?: EnumSignTagTypes; dimensions?: any }): void {
    if (this.isToolbarItemDisabled(item)) return;
    if (item.kind === 'tag' && !item.type) return;
    const touch = event.touches[0];
    if (!touch) return;
    this._suppressPointerFromTouch = true;
    this._beginTouchDrag(touch.clientX, touch.clientY, item);
  }

  onToolbarItemPointerDown(event: PointerEvent, item: { kind: 'signature' | 'comment' | 'tag'; type?: EnumSignTagTypes; dimensions?: any }): void {
    if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
    if (this._suppressPointerFromTouch && event.pointerType === 'touch') return;
    if (this.isToolbarItemDisabled(item)) return;
    if (item.kind === 'tag' && !item.type) return;
    event.preventDefault();
    this._beginPointerDrag(event.clientX, event.clientY, item);
  }

  private _beginTouchDrag(clientX: number, clientY: number, item: { kind?: 'signature' | 'comment' | 'tag'; type?: EnumSignTagTypes; dimensions?: any }): void {
    this._didDropThisDrag = false;
    this._dragActive = false;
    this._dragStartPoint = { x: clientX, y: clientY };
    this._touchDragItem = { type: item.type, dimensions: item.dimensions };
    this._touchDragKind = item.kind;
    this._prevBodyTouchAction = document.body.style.touchAction;
    document.body.style.touchAction = 'none';

    this._touchMoveListener = (e: TouchEvent) => {
      const moveTouch = e.touches[0];
      if (!moveTouch) return;
      e.preventDefault();
      if (!this._dragActive) {
        const dx = Math.abs(moveTouch.clientX - (this._dragStartPoint?.x ?? moveTouch.clientX));
        const dy = Math.abs(moveTouch.clientY - (this._dragStartPoint?.y ?? moveTouch.clientY));
        if (dx + dy < 8) return;
        this._dragActive = true;
        this._setNextButtonSuppressedForTagDrag(true);
          const label = this._touchDragKind === 'signature' ? 'Signature' : this._touchDragKind === 'comment' ? 'Comment' : this._touchDragItem!.type;
          this._createDragGhost(moveTouch.clientX, moveTouch.clientY, label as any);
      }
      this._moveDragGhost(moveTouch.clientX, moveTouch.clientY);
    };

    this._touchEndListener = (e: TouchEvent) => {
      const endTouch = e.changedTouches[0];
      if (endTouch && this._dragActive) {
        this._dropAtPoint(endTouch.clientX, endTouch.clientY);
      }
      this._setNextButtonSuppressedForTagDrag(false, 180);
      this._suppressPointerFromTouch = false;
      this._cleanupTouchDrag();
    };

    window.addEventListener('touchmove', this._touchMoveListener, { passive: false });
    window.addEventListener('touchend', this._touchEndListener, { passive: false });
    window.addEventListener('touchcancel', this._touchEndListener, { passive: false });
  }

  private _beginPointerDrag(clientX: number, clientY: number, item: { kind?: 'signature' | 'comment' | 'tag'; type?: EnumSignTagTypes; dimensions?: any }): void {
    this._didDropThisDrag = false;
    this._dragActive = false;
    this._dragStartPoint = { x: clientX, y: clientY };
    this._touchDragItem = { type: item.type, dimensions: item.dimensions };
    this._touchDragKind = item.kind;

    this._pointerMoveListener = (e: PointerEvent) => {
      e.preventDefault();
      if (!this._dragActive) {
        const dx = Math.abs(e.clientX - (this._dragStartPoint?.x ?? e.clientX));
        const dy = Math.abs(e.clientY - (this._dragStartPoint?.y ?? e.clientY));
        if (dx + dy < 8) return;
        this._dragActive = true;
        this._setNextButtonSuppressedForTagDrag(true);
        const label = this._touchDragKind === 'signature' ? 'Signature' : this._touchDragKind === 'comment' ? 'Comment' : this._touchDragItem!.type;
        this._createDragGhost(e.clientX, e.clientY, label as any);
      }
      this._moveDragGhost(e.clientX, e.clientY);
    };

    this._pointerUpListener = (e: PointerEvent) => {
      if (this._dragActive) {
        this._dropAtPoint(e.clientX, e.clientY);
      }
      this._setNextButtonSuppressedForTagDrag(false, 180);
      this._cleanupTouchDrag();
    };

    window.addEventListener('pointermove', this._pointerMoveListener, { passive: false });
    window.addEventListener('pointerup', this._pointerUpListener, { passive: false });
    window.addEventListener('pointercancel', this._pointerUpListener, { passive: false });
  }

  private _createDragGhost(clientX: number, clientY: number, label: EnumSignTagTypes | string): void {
    const ghost = document.createElement('div');
    ghost.className = 'touch-drag-ghost';
    const key = typeof label === 'string' ? label : String(label);
    const text = this.t(key.toLowerCase()) || key;
    ghost.textContent = text;
    ghost.style.position = 'fixed';
    ghost.style.left = `${clientX - 30}px`;
    ghost.style.top = `${clientY - 20}px`;
    ghost.style.zIndex = '99999';
    ghost.style.pointerEvents = 'none';
    ghost.style.padding = '6px 10px';
    ghost.style.borderRadius = '6px';
    ghost.style.background = '#ffffff';
    ghost.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';
    ghost.style.fontSize = '12px';
    ghost.style.color = '#374151';
    document.body.appendChild(ghost);
    this._touchDragGhost = ghost;
    this._ghostLastX = clientX;
    this._ghostLastY = clientY;
  }

  private _moveDragGhost(clientX: number, clientY: number): void {
    if (!this._touchDragGhost) return;
    this._ghostLastX = clientX;
    this._ghostLastY = clientY;
    if (this._ghostRafId !== null) return;
    this._ghostRafId = window.requestAnimationFrame(() => {
      if (!this._touchDragGhost) return;
      this._touchDragGhost.style.left = `${this._ghostLastX - 30}px`;
      this._touchDragGhost.style.top = `${this._ghostLastY - 20}px`;
      this._ghostRafId = null;
    });
  }

  private _dropAtPoint(clientX: number, clientY: number): void {
    if (!this._touchDragItem) return;
    if (this._didDropThisDrag) return;
    this._didDropThisDrag = true;
    if (this._touchDragKind === 'signature' || this._touchDragKind === 'comment') {
      this.createSignPlaceholderDropAtPoint(clientX, clientY, this._touchDragKind);
      return;
    }
    if (!this._touchDragItem.type) return;
    const elements = document.elementsFromPoint(clientX, clientY) as HTMLElement[];
    const textLayer = elements.find((el) => el.classList?.contains('textLayer')) as HTMLElement | undefined;
    if (!textLayer) return;
    this.generateSignTag(
      { clientX, clientY } as MouseEvent,
      textLayer,
      this._touchDragItem.type,
      this._touchDragItem.dimensions
    );
  }

  private _cleanupTouchDrag(): void {
    if (this._touchMoveListener) {
      window.removeEventListener('touchmove', this._touchMoveListener);
      this._touchMoveListener = undefined;
    }
    if (this._touchEndListener) {
      window.removeEventListener('touchend', this._touchEndListener);
      window.removeEventListener('touchcancel', this._touchEndListener);
      this._touchEndListener = undefined;
    }
    if (this._pointerMoveListener) {
      window.removeEventListener('pointermove', this._pointerMoveListener);
      this._pointerMoveListener = undefined;
    }
    if (this._pointerUpListener) {
      window.removeEventListener('pointerup', this._pointerUpListener);
      window.removeEventListener('pointercancel', this._pointerUpListener);
      this._pointerUpListener = undefined;
    }
    if (this._touchDragGhost?.parentNode) {
      this._touchDragGhost.parentNode.removeChild(this._touchDragGhost);
    }
    this._touchDragGhost = undefined;
    if (this._prevBodyTouchAction !== undefined) {
      document.body.style.touchAction = this._prevBodyTouchAction;
      this._prevBodyTouchAction = undefined;
    }
    if (this._ghostRafId !== null) {
      window.cancelAnimationFrame(this._ghostRafId);
      this._ghostRafId = null;
    }
    this._touchDragItem = undefined;
    this._touchDragKind = undefined;
    this._dragStartPoint = undefined;
    this._dragActive = false;
  }

  isToolbarItemDisabled(item: { kind: 'signature' | 'comment' | 'tag' }): boolean {
    if (item.kind === 'comment') return false;
    return this.signerRole?.toLowerCase() !== EnumSignerRoleType.SIGNER.toLowerCase();
  }

  constructor(
    private dialog: MatDialog,
    private _viewContainerRef: ViewContainerRef,
    public signService: SignService,
    private _appService: AppService
  ) {
    effect(() => {
      if (this._headerBeforeLoginService.kapybaraOpenSidebarRequest()) {
        this.kapybaraSidebarOpen = true;
        this._headerBeforeLoginService.clearKapybaraOpenSidebarRequest();
        this.updateKapybaraSuggestedQuestions();
        this.loadPdfInsights();
        this.ensureKapybaraChatSeeded();
        if (!this._kapybaraSidebarOpenedBefore) {
          this._kapybaraSidebarOpenedBefore = true;
          this.scrollKapybaraSidebarToTop();
        }
        this._cdr.detectChanges();
      }
    });
  }

  ngOnInit(): void {
    this.updateMobileViewState();
    this.signService.registerNextDragSuppressHandler((suppressed, delayMs) => {
      this._setNextButtonSuppressedForTagDrag(suppressed, delayMs ?? 0);
    });
    this._signToolbarService.setExpanded(false);
    this._toolbarSubscription.add(
      this._signToolbarService.expanded$.subscribe((expanded) => {
        this.isMobileToolbarExpanded = expanded;
        this._cdr.detectChanges();
      })
    );
    this._toolbarSubscription.add(
      this._signToolbarService.action$.subscribe((action: SignToolbarAction) => {
        this.onToolbarItemClick(action);
      })
    );
    this.selectedLanguage = this._languageService.currentLanguage;
    this._langSubscription = this._languageService.language$.subscribe((lang) => {
      this.selectedLanguage = lang;
      this.updateScrollIndicatorText();
    });
    const langChange$ = (this._translate as any)?.onLangChange;
    if (langChange$?.subscribe) {
      this._translateSubscription = langChange$.subscribe(() => {
        this.updateScrollIndicatorText();
      });
    }
    this.isView = this._activatedRoute.snapshot.data["isView"] ?? false;
    if (this.isView) {
      // Stable full-document scroll height — infinite-scroll + repeated layout
      // fixes were causing large view-mode PDFs to jump back to the top.
      this.pageViewMode = 'multiple';
    }
    this._signToolbarService.setActive(!this.isView);
    this._headerBeforeLoginService.setViewBannerVisible(this.isView);
    this._headerBeforeLoginService.setHeaderVisible(true);
    this._fileRequestID = this._activatedRoute.snapshot.params["requestID"];
    this._signerID = this._activatedRoute.snapshot.params["signerID"];
    const params = {
      SignRequestId: this._fileRequestID,
      ...(!this.isView ? { SignerId: this._signerID } : {}),
      // appToken,
    };
    this._httpService
      .get(
        this._httpService.createEndpoint(
          this.isView
            ? Constants.API_URI_SIGN_DOWNLOAD_VIEW
            : Constants.API_URI_SIGN_DOWNLOAD_SIGN,
          undefined,
          params
        )
      )
      .subscribe(
        (response: any) => {
          this._ERSDDialogData = {
            requesterName: response.Data.RequesterName,
            customerName: response.Data.CustomerName,
            requesterMessage: response.Data.RequesterMessage,
            EULAMessage: response.Data.EULAMessage,
            openDeclineConfirmationDialog: () => this.openDeclineConfirmationDialog(true)
          };
          if (!this.isView) {
            // this.openERSDDialog(this._ERSDDialogData);
            this.isERSDAccepted = true;
          }
          this.base64 = response.Data?.DocContent;
          this.signService.signTags = SignService.parseSignTagsFromApi(response.Data?.Tags);
          this.signService.syncRequiredFieldsCount(this._countedSignHereTagIds);
          // Fresh document load → drop any signatures saved from a previous
          // session so they don't get re-attached to spans in this PDF, and
          // reset per-page required counts / dedup set so counts are
          // rebuilt from scratch for the new document.
          this.signService.clearPlacedSignatures();
          this._requiredSignatureHereCount = {};
          this._countedSignHereTagIds = new Set();
          this._viewModeScrollLayoutDone = false;
          this._viewModeLastAvailableH = 0;
          this.pdfReady = false;
          this._pdfLoaded = false;
          this.isPdfFullyScrolled = false;
          this.showAdditionalInfo = false;
          this.drawerOpenedByUser = false;
          this._endPanelDelayPassed = false;
          this._userHasScrolledDocument = false;
          this._cancelSignPdfReadyTimers();
          this._cancelViewModePdfReadyWatch();
          const signerNameInitials = response.Data?.SignerName?.match(/\b(\w)/g)?.join('')?.trim()?.toUpperCase()?.substring(0, 2);
          this._headerBeforeLoginService.updateUserInitials(signerNameInitials);
          this._headerBeforeLoginService.updateHeaderLogo(
            this._resolveHeaderImageSrc(
              response.Data?.HeaderImage ??
                response.Data?.headerImage ??
                response.Data?.HeaderLogo ??
                response.Data?.headerLogo ??
                response.HeaderImage ??
                response.headerImage ??
                response.HeaderLogo ??
                response.headerLogo,
            ),
          );
          this.signerRole = response.Data?.SignerRole;
          this.signerName = response.Data?.SignerName;
          this._signToolbarService.setSignerRole(this.signerRole ?? null);
          this._signToolbarService.setSignerActions(response.Data?.SignerActions);
          this.signService.roleText = this.signerRole?.toLowerCase() === EnumSignerRoleType.REVIEWER.toLowerCase() ? 'Reviewer' : 'Signer';
          this._cdr.detectChanges();
          this.isSigned =
            response.Data?.RequestStatus === EnumRequestStatus.SIGNED ||
            (response.Data?.RequestStatus ===
              EnumRequestStatus.PARTIAL_SIGNED &&
              response.Data?.SignedDate);
          if (this.isSigned) {
            this.statusMessage = {
              heading: this.t('alreadySignedHeading'),
              message: this.t('alreadySignedMessage'),
              isSuccess: true,
            };
            if (!this.isView) {
              this._headerBeforeLoginService.setHeaderVisible(false);
              this._signToolbarService.setActive(false);
            }
          } else {
            this.signService.getSignerSignatures(this._fileRequestID, this._signerID);
          }
          this.fileName =
            response.Data?.DocName || `eSign-${this._fileRequestID}`;
          this._order = response.Data.Order?.toString();
          this._signatureTag =
            Constants.SIGNATURE_TAG_PREFIX + response.Data.Order;
          this._signatureDynamicTag =
            Constants.SIGNATURE_DYNAMIC_TAG_PREFIX + response.Data.Order;
          this.isDownloading = false;
          if (this.isView) {
            this._cdr.detectChanges();
            this._startViewModePdfReadyWatch();
          }
        },
        (errorResponse) => {
          this.isDownloading = false;
          this.isError = true;
          const errorBody = errorResponse?.error ?? {};
          const failRule = errorBody?.FailRules?.[0];
          const failCode = (failRule?.Code || '').toString();
          const failMsg = (failRule?.Msg || '').toString();
          const errorData = errorBody?.Data ?? {};
          const isAlreadySubmitted =
            /actioned already|already (been )?submitted|already signed/i.test(failMsg) ||
            /ALREADY|ACTIONED/i.test(failCode);

          if (isAlreadySubmitted) {
            const apiCustomMessage = (
              errorData?.CustomMessage ??
              errorData?.customMessage ??
              ''
            ).toString().trim();
            const resolvedHeaderImage = this._resolveHeaderImageSrc(
              errorData?.HeaderImage ??
                errorData?.headerImage ??
                errorData?.HeaderLogo ??
                errorData?.headerLogo,
            );
            this._headerBeforeLoginService.updateHeaderLogo(resolvedHeaderImage);
            this._headerBeforeLoginService.setHeaderVisible(true);
            this._signToolbarService.setActive(false);
            this.statusMessage = {
              isSuccess: true,
              heading: apiCustomMessage ? this.t('alreadySignedHeading') : '',
              message: apiCustomMessage || failMsg,
            };
            this._cdr.detectChanges();
            return;
          }

          this._headerBeforeLoginService.updateHeaderLogo(null);
          this.statusMessage = {
            isSuccess: false,
            heading:
              failCode === "NOSIGNREQUEST"
                ? this.t('invalidLinkHeading')
                : "",
            message:
              failCode === "NOSIGNREQUEST"
                ? this.t('invalidLinkMessage')
                : failMsg || Messages.MSG_ERROR_UNEXPECTED,
          };
        }
      );
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.setupScrollDetection();
    }, 2000);
  }

  ngOnDestroy(): void {
    this._disablePinchZoomGuard();
    this._overlayZoomLockCount = 1;
    this._unlockOverlayAgainstZoom();
    this._detachSignLayoutObserver();
    if (this._signPlacementTimer) {
      clearTimeout(this._signPlacementTimer);
      this._signPlacementTimer = null;
    }
    this._signPagePlacementTimers.forEach((t) => clearTimeout(t));
    this._signPagePlacementTimers.clear();
    if (this._tagLayoutRestoreTimer) {
      clearTimeout(this._tagLayoutRestoreTimer);
      this._tagLayoutRestoreTimer = null;
    }
    this._clearZoomReplacementBurst();
    this._stopSignTagWatchdog();
    this._signToolbarService.setActive(false);
    this._signToolbarService.clearSignerActions();
    this._headerBeforeLoginService.setViewBannerVisible(false);
    this._headerBeforeLoginService.setHeaderVisible(true);
    this.signService.registerNextDragSuppressHandler(null);
    if (this._nextRestoreAfterTagDragTimer) {
      clearTimeout(this._nextRestoreAfterTagDragTimer);
      this._nextRestoreAfterTagDragTimer = null;
    }
    this._toolbarSubscription.unsubscribe();
    this._scrollSubscription.unsubscribe();
    this._langSubscription.unsubscribe();
    this._translateSubscription.unsubscribe();
    this.signService.clearPlacedSignatures();
    this._cancelViewModePdfReadyWatch();
    this._cancelSignPdfReadyTimers();
  }

  
  private _startViewModePdfReadyWatch(): void {
    if (!this.isView || this.pdfReady) return;
    this._cancelViewModePdfReadyWatch();
    const seq = ++this._pdfReadyWatchSeq;

    const tryDismissLoader = (): void => {
      if (seq !== this._pdfReadyWatchSeq || this.pdfReady || !this.isView) return;
      const hasRenderedPage = !!document.querySelector('#viewerContainer .page');
      if (this._pdfLoaded || hasRenderedPage) {
        this.markPdfReady();
      }
    };

    [300, 700, 1200, 2000, 3500, 5000].forEach((ms) => setTimeout(tryDismissLoader, ms));

    this._pdfReadyHardFallbackTimer = setTimeout(() => {
      if (seq !== this._pdfReadyWatchSeq || this.pdfReady || !this.isView) return;
      this.markPdfReady();
    }, 8000);
  }

  private _cancelViewModePdfReadyWatch(): void {
    this._pdfReadyWatchSeq++;
    if (this._pdfReadyHardFallbackTimer) {
      clearTimeout(this._pdfReadyHardFallbackTimer);
      this._pdfReadyHardFallbackTimer = null;
    }
  }

  private _viewModeLayoutTimer: ReturnType<typeof setTimeout> | null = null;
  /** Latch: view-mode scroll layout runs once (+ on resize), not on every page render. */
  private _viewModeScrollLayoutDone = false;
  private _viewModeLastAvailableH = 0;
  private _viewModeOverflowTimer: ReturnType<typeof setTimeout> | null = null;
  private _pdfReadyWatchSeq = 0;
  private _pdfReadyHardFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private _signPdfReadyTimer: ReturnType<typeof setTimeout> | null = null;
  private _signPdfReadyFallbackTimer: ReturnType<typeof setTimeout> | null = null;

  private _setupViewModeScrollLayout(force = false): void {
    if (!this.isView || this.isDownloading || !this.base64) return;

    const apply = (): void => {
      const header = document.querySelector('.sign-header') as HTMLElement | null;
      const footer = document.querySelector('.footer-sign') as HTMLElement | null;
      const mobileBanner = document.querySelector('.view-mobile-banner') as HTMLElement | null;
      const pdfContainer = document.querySelector('.pdf-viewer-container.view-mode') as HTMLElement | null;
      const pdfViewerEl = document.querySelector('ngx-extended-pdf-viewer') as HTMLElement | null;
      const outerContainer = document.getElementById('outerContainer');
      const viewerContainer = document.getElementById('viewerContainer');

      if (!pdfContainer || !outerContainer || !viewerContainer) return;

      const headerH = header?.offsetHeight ?? 0;
      const footerH = footer?.offsetHeight ?? 0;
      const isMobileViewMode = !!mobileBanner && window.innerWidth <= 768;
      const bannerMargin = mobileBanner
        ? (() => {
            const s = window.getComputedStyle(mobileBanner);
            return (
              mobileBanner.offsetHeight +
              (parseFloat(s.marginTop) || 0) +
              (parseFloat(s.marginBottom) || 0)
            );
          })()
        : 0;
      const signMain = pdfContainer.closest('.sign-main') as HTMLElement | null;
      let available: number;
      if (isMobileViewMode && signMain?.clientHeight) {
        available = Math.max(signMain.clientHeight - bannerMargin, 200);
      } else {
        available = Math.max(window.innerHeight - headerH - footerH - bannerMargin, 200);
      }

      if (
        !force &&
        this._viewModeScrollLayoutDone &&
        Math.abs(available - this._viewModeLastAvailableH) < 4
      ) {
        return;
      }

      const savedScrollTop = viewerContainer.scrollTop;

      pdfContainer.style.position = 'relative';
      pdfContainer.style.flex = '1 1 auto';
      pdfContainer.style.minHeight = '0';
      pdfContainer.style.overflow = 'hidden';
      pdfContainer.style.height = `${available}px`;
      pdfContainer.style.maxHeight = `${available}px`;
      pdfContainer.style.paddingBottom = '0';

      if (pdfViewerEl) {
        pdfViewerEl.style.position = 'relative';
        pdfViewerEl.style.flex = '1 1 auto';
        pdfViewerEl.style.minHeight = '0';
        pdfViewerEl.style.height = '100%';
        pdfViewerEl.style.maxHeight = '100%';
        pdfViewerEl.style.overflow = 'hidden';
      }

      outerContainer.style.position = 'absolute';
      outerContainer.style.top = '0';
      outerContainer.style.left = '0';
      outerContainer.style.right = '0';
      outerContainer.style.width = '100%';
      outerContainer.style.height = '100%';
      outerContainer.style.maxHeight = '100%';
      outerContainer.style.overflow = 'hidden';

      viewerContainer.style.position = 'absolute';
      viewerContainer.style.top = '0';
      viewerContainer.style.left = '0';
      viewerContainer.style.right = '0';
      viewerContainer.style.bottom = '0';
      viewerContainer.style.height = '100%';
      viewerContainer.style.maxHeight = '100%';
      viewerContainer.style.minHeight = '0';
      viewerContainer.style.overflow = 'auto';
      viewerContainer.style.setProperty('-webkit-overflow-scrolling', 'touch');
      if (isMobileViewMode) {
        viewerContainer.style.paddingTop = '12px';
        viewerContainer.style.boxSizing = 'border-box';
      } else {
        viewerContainer.style.paddingTop = '';
        viewerContainer.style.boxSizing = '';
      }

      const fixMobileBannerOverlap = (): void => {
        if (!mobileBanner || !isMobileViewMode) {
          pdfContainer.style.marginTop = '';
          return;
        }
        const overlap = mobileBanner.getBoundingClientRect().bottom - pdfContainer.getBoundingClientRect().top;
        if (overlap > 0.5) {
          const offset = Math.ceil(overlap);
          pdfContainer.style.marginTop = `${offset}px`;
          const h = parseFloat(pdfContainer.style.height || '0');
          if (h > 0) {
            const nextH = Math.max(h - offset, 200);
            pdfContainer.style.height = `${nextH}px`;
            pdfContainer.style.maxHeight = `${nextH}px`;
          }
        } else {
          pdfContainer.style.marginTop = '';
        }
      };
      fixMobileBannerOverlap();

      const zoom = document.querySelector('ngx-extended-pdf-viewer .zoom') as HTMLElement | null;
      if (zoom) zoom.style.height = '100%';

      this._viewModeLastAvailableH = available;
      this._viewModeScrollLayoutDone = true;

      if (savedScrollTop > 0) {
        requestAnimationFrame(() => {
          viewerContainer.scrollTop = savedScrollTop;
        });
      }

      if (isMobileViewMode) {
        requestAnimationFrame(fixMobileBannerOverlap);
      }
    };

    if (this._viewModeScrollLayoutDone && !force) {
      apply();
      return;
    }

    apply();
    requestAnimationFrame(apply);
    [120, 400, 800].forEach((ms) => setTimeout(apply, ms));
  }

  private _scheduleViewModeScrollLayout(force = false): void {
    if (!this.isView || this.isDownloading || !this.base64) return;
    if (this._viewModeScrollLayoutDone && !force) return;
    if (this._viewModeLayoutTimer) clearTimeout(this._viewModeLayoutTimer);
    this._viewModeLayoutTimer = setTimeout(() => {
      this._viewModeLayoutTimer = null;
      this._setupViewModeScrollLayout(force);
    }, 50);
  }

  private setupScrollDetection(): void {
    if (this.isView) return;

    const selectors = ['#viewerContainer', '#outerContainer', '.pdfViewer'];
    let viewerContainer: HTMLElement | null = null;
    
    for (const selector of selectors) {
      viewerContainer = document.querySelector(selector);
      if (viewerContainer) {
        
        break;
      }
    }
    
    if (viewerContainer) {
      
      const scroll$ = fromEvent(viewerContainer, 'scroll').pipe(
        debounceTime(100)
      );
      
      this._scrollSubscription = scroll$.subscribe(() => {
        if (viewerContainer!.scrollTop > 12) this._userHasScrolledDocument = true;
        this.checkScrollPosition(viewerContainer!);
      });
      
      
      const wheel$ = fromEvent(viewerContainer, 'wheel').pipe(
        debounceTime(150)
      );
      
      this._scrollSubscription.add(wheel$.subscribe(() => {
        setTimeout(() => this.checkScrollPosition(viewerContainer!), 100);
      }));
      
      
      setTimeout(() => this.checkScrollPosition(viewerContainer!), 500);
    } else {
      
      
      setTimeout(() => this.setupScrollDetection(), 1000);
    }
  }

  private checkScrollPosition(viewerContainer: HTMLElement): void {
    if (this.isView || !this.pdfReady || !this._endPanelDelayPassed) return;

    const scrollTop = viewerContainer.scrollTop;
    const scrollHeight = viewerContainer.scrollHeight;
    const clientHeight = viewerContainer.clientHeight;

    if (scrollTop > 12) this._userHasScrolledDocument = true;

    // Before pages finish layout, scrollHeight ≈ clientHeight and looks "at bottom".
    if (scrollHeight < 120) return;
    if (this._pagesCount > 1 && scrollHeight < clientHeight + 80) return;

    const fitsInViewport = scrollHeight <= clientHeight + 32;
    const isScrollable = !fitsInViewport;
    const scrollBottom = scrollTop + clientHeight;
    const isAtAbsoluteBottom = Math.abs(scrollHeight - scrollBottom) <= 10;

    if (isAtAbsoluteBottom && !this.isPdfFullyScrolled) {
      // Short PDFs that fit on screen: only prompt — never auto-open submit panel.
      if (fitsInViewport && !this._userHasScrolledDocument) {
        this.showScrollIndicator(true, true);
        return;
      }

      this.isPdfFullyScrolled = true;
      this._cdr.detectChanges();
      this.showScrollIndicator(true, true);

      if (isScrollable) {
        setTimeout(() => {
          viewerContainer.scrollTop += 10;
        }, 300);
      }
    } else if (!isAtAbsoluteBottom && this.isPdfFullyScrolled) {

      this.isPdfFullyScrolled = false;
      this.showAdditionalInfo = false;
      this._cdr.detectChanges();
      this.showScrollIndicator(true);
    }
  }


  private _isLastPageRendered(): boolean {
    if (!this._pdfLoaded || !this._pagesCount) return false;
    const lastPage = document.querySelector(
      `.page[data-page-number="${this._pagesCount}"]`,
    );
    if (!lastPage) return false;
    return !!lastPage.querySelector('canvas, .textLayer, .canvasWrapper');
  }

  private showScrollIndicator(show: boolean, force: boolean = false): void {
    if (this.isView) return;
    if (show && (!this._pdfLoaded || !this.pdfReady)) return;
    if (show && this.isSinglePage && this.isMobileView && this.drawerOpenedByUser) return;
    let indicator = document.getElementById('scroll-indicator');
    
    if (show && (!this.isPdfFullyScrolled || force)) {
      if (!indicator) {
        indicator = document.createElement('div');
        indicator.id = 'scroll-indicator';
        indicator.className = 'scroll-indicator';
        indicator.innerHTML = `
          <div class="scroll-indicator__icon">⬇</div>
          <div class="scroll-indicator__text">
            <span>${this.t('scrollCtaBody')}</span>
          </div>
        `;
        const handleIndicatorAction = (e?: Event): void => {
          if (e) {
            e.preventDefault();
            e.stopPropagation();
          }
          if (this.isSinglePage && this.isMobileView) {
            this.drawerOpenedByUser = true;
            this.showScrollIndicator(false);
            this._cdr.detectChanges();
          } else {
            this.isPdfFullyScrolled = true;
            this.showAdditionalInfo = true;
            this.showScrollIndicator(false);
            this._cdr.detectChanges();
            const viewerContainer = document.querySelector('#viewerContainer') || 
                                   document.querySelector('#outerContainer') || 
                                   document.querySelector('.pdfViewer');
            if (viewerContainer) {
              viewerContainer.scrollTop = viewerContainer.scrollHeight;
            }
          }
        };
        indicator.addEventListener('click', (e) => handleIndicatorAction(e));
        indicator.addEventListener('touchstart', (e) => handleIndicatorAction(e), { passive: false });
        document.querySelector('.pdf-viewer-container')?.appendChild(indicator);
        
       
        const icon = indicator.querySelector('mat-icon');
        if (icon) {
          icon.setAttribute('fontIcon', 'keyboard_arrow_down');
        }
      } else {
        this.updateScrollIndicatorText();
      }
      
      setTimeout(() => {
        if (indicator) {
          indicator.classList.add('visible');
        }
      }, 100);
    } else {
      if (indicator) {
        indicator.classList.remove('visible');
        setTimeout(() => {
          if (indicator && indicator.parentNode) {
            indicator.parentNode.removeChild(indicator);
          }
        }, 300);
      }
    }
  }

  private updateScrollIndicatorText(): void {
    const indicator = document.getElementById('scroll-indicator');
    if (!indicator) return;
    const textSpan = indicator.querySelector('.scroll-indicator__text span');
    if (textSpan) {
      textSpan.textContent = this.t('scrollCtaBody');
    }
  }

  private _shouldUseMultiplePageView(): boolean {
    return this._pagesCount > 5 || (this.signService.signTags?.length ?? 0) > 0;
  }

  private _canPlaceSignTags(): boolean {
    if (this.isSigned || this.isView) return false;
    return true;
  }

  pagesLoaded(event: PagesLoadedEvent) {
    this._pagesCount = event.pagesCount;
    this._pdfLoaded = true;
    this._signTargetCursor = -1;
    this._signTargetPrimeIndex = 0;
    this._cdr.detectChanges();
    this._resetPdfScrollToStart();
    void this._buildSignTargets();
    if (this.isView) {
      this.loadPdfSuggestions();
    }
    if (this.kapybaraSidebarOpen) {
      this.loadPdfInsights();
    }
    if (!this.isView) {
      if (this._shouldUseMultiplePageView()) {
        this.pageViewMode = 'multiple';
        this._signSetupDone = true;
      }
      const apiHasSignature = this.signService.signTags?.some(
        (t) => t.Type === EnumSignTagTypes.SIGNATURE,
      ) ?? false;
      if (apiHasSignature && !this.isReviewer) {
        this._signHereDetectedEver = true;
        this.hasSignHereElements = true;
        setTimeout(() => this._startSignTagWatchdog(), 200);
      }
      queueMicrotask(() => this._placeAndBindSignTags());
      setTimeout(() => this._placeAndBindSignTags(), 300);
      this._captureFitZoomFloor();
      this._scheduleSignPdfReadyFallback();
      setTimeout(() => {
        if (this._isPdfContentLaidOut()) {
          this.markPdfReady();
        }
      }, 80);
      setTimeout(() => {
        if (!this.pdfReady || !this._endPanelDelayPassed) return;
        const viewerContainer = this._getViewerContainer();
        if (viewerContainer) {
          this.checkScrollPosition(viewerContainer);
        }
      }, 1500);
    }

    if (this.isView) {
      if (this._canPlaceSignTags()) {
        if (this._shouldUseMultiplePageView()) {
          this.pageViewMode = 'multiple';
        }
        queueMicrotask(() => this._placeAndBindSignTags());
        setTimeout(() => this._placeAndBindSignTags(), 300);
        setTimeout(() => this._placeAndBindSignTags(), 1200);
      }
      this._scheduleViewModeScrollLayout();
      this._fixOverflowClipping();
      this.markPdfReady();
    }
  }

  onPageRendered(event: PageRenderedEvent): void {
    if (!this.pdfReady) {
      if (this.isView) {
        this.markPdfReady();
      } else if (this._pdfLoaded) {
        this._scheduleSignPdfReadyAfterRender();
      }
    }

    if (!this.isView) {
      this._fixOverflowClipping();
      this._ensureSignChromeReady();
      this._scheduleSignTagPlacement(60);
      if (event?.pageNumber) {
        this._scheduleSignTagPlacementForPage(event.pageNumber, 120);
      }

      if (!this.pdfReady && this._isPdfContentLaidOut()) {
        this.markPdfReady();
      }

      if (this.pdfReady && event.pageNumber === this._pagesCount && this._pdfLoaded) {
        setTimeout(() => {
          const viewerContainer = document.querySelector('#viewerContainer') ||
                                 document.querySelector('#outerContainer') ||
                                 document.querySelector('.pdfViewer');
          if (viewerContainer) {
            this.checkScrollPosition(viewerContainer as HTMLElement);
          }
        }, 400);
      }
      return;
    }

    this._fixOverflowClipping();

    if (this.isView) {
      if (this._canPlaceSignTags() && event?.pageNumber) {
        this._scheduleSignTagPlacementForPage(event.pageNumber, 120);
      }
      if (this.isMobileView) {
        if (this._viewModeLayoutTimer) clearTimeout(this._viewModeLayoutTimer);
        this._viewModeLayoutTimer = setTimeout(() => {
          this._viewModeLayoutTimer = null;
          this._viewModeScrollLayoutDone = false;
          this._setupViewModeScrollLayout(true);
        }, 80);
      }
    }
  }

  onTextLayerRendered(event: TextLayerRenderedEvent): void {
    if (this._canPlaceSignTags() && this._pdfLoaded) {
      queueMicrotask(() => this._placeAndBindSignTags());
      if (!this.pdfReady && this._isPdfContentLaidOut()) {
        this.markPdfReady();
      }
    }
    if (this._canPlaceSignTags()) {
      this._scheduleSignTagPlacement(100);
    }
    if (this._canPlaceSignTags() && event?.pageNumber) {
      this._scheduleSignTagPlacementForPage(event.pageNumber, 150);
    }
  }

  private _scheduleSignTagPlacementForPage(pageNumber: number, delayMs: number): void {
    if (!this._canPlaceSignTags() || !this._pdfLoaded) return;
    const prev = this._signPagePlacementTimers.get(pageNumber);
    if (prev) clearTimeout(prev);
    this._signPagePlacementTimers.set(
      pageNumber,
      setTimeout(() => {
        this._signPagePlacementTimers.delete(pageNumber);
        this._placeAndBindSignTags();
        if (this._countVisibleSignHerePlaceholders() > 0) {
          this._stopSignTagWatchdog();
        }
      }, delayMs),
    );
  }

  private _scheduleSignTagPlacement(delayMs: number = 60): void {
    if (!this._canPlaceSignTags() || !this._pdfLoaded) return;
    if (this._signPlacementTimer) clearTimeout(this._signPlacementTimer);
    this._signPlacementTimer = setTimeout(() => {
      this._signPlacementTimer = null;
      this._placeAndBindSignTags();
      if (this._countVisibleSignHerePlaceholders() > 0) {
        this._stopSignTagWatchdog();
      }
    }, delayMs);
  }

  private _fixOverflowClipping(): void {
    if (this.isView) {
      if (this._viewModeOverflowTimer) clearTimeout(this._viewModeOverflowTimer);
      this._viewModeOverflowTimer = setTimeout(() => {
        this._viewModeOverflowTimer = null;
        this._applyPdfTagOverflowFix();
      }, 250);
      return;
    }

    const wave = ++this._overflowFixSeq;
    [0, 120, 400, 900, 1600].forEach((ms) => {
      setTimeout(() => {
        if (wave !== this._overflowFixSeq) return;
        this._applyPdfTagOverflowFix();
      }, ms);
    });
  }

  private _applyPdfTagOverflowFix(): void {
    const setOv = (el: HTMLElement): void =>
      el.style.setProperty('overflow', 'visible', 'important');

    document.querySelectorAll('.textLayer').forEach((el) => setOv(el as HTMLElement));
    document.querySelectorAll('.canvasWrapper').forEach((el) => setOv(el as HTMLElement));
    document.querySelectorAll('.annotationLayer').forEach((el) => setOv(el as HTMLElement));
    document.querySelectorAll('.pdfViewer .page').forEach((el) => setOv(el as HTMLElement));

    document.querySelectorAll('.sign-tag-element-container').forEach((el) => setOv(el as HTMLElement));
    document.querySelectorAll('.sign-tag-comment-container').forEach((el) => setOv(el as HTMLElement));
    document.querySelectorAll('.box-sign-here').forEach((el) => setOv(el as HTMLElement));
    document.querySelectorAll('.resizable').forEach((el) => {
      const host = (el as HTMLElement).closest('.sign-tag-element-container, .box-sign-here');
      if (host) setOv(el as HTMLElement);
    });

    if (this.isView) {
      document.querySelectorAll('.mdc-notched-outline').forEach((el) => {
        (el as HTMLElement).style.setProperty('display', 'none', 'important');
      });
      document.querySelectorAll('.mat-mdc-form-field-subscript-wrapper').forEach((el) => {
        (el as HTMLElement).style.setProperty('display', 'none', 'important');
      });
      document.querySelectorAll('.mat-datepicker-toggle').forEach((el) => {
        (el as HTMLElement).style.setProperty('display', 'none', 'important');
      });
    }
  }

  
  @HostListener('window:resize')
  onWindowResize() {
    this.updateMobileViewState();
    // Fit-to-width scale changes with viewport width — rescale the zoom floor.
    this._rescaleZoomFloorForWidth();
    if (this.isView && !this.isDownloading && this.base64) {
      this._setupViewModeScrollLayout(true);
    } else if (!this.isView) {
      this._scheduleSignTagsLayoutRestore(200);
      setTimeout(() => {
        const viewerContainer = document.querySelector('#viewerContainer') ||
                               document.querySelector('#outerContainer') ||
                               document.querySelector('.pdfViewer');
        if (viewerContainer) {
          this.checkScrollPosition(viewerContainer as HTMLElement);
        }
      }, 300);
    }
  }

  private updateMobileViewState(): void {
    this.isMobileView = window.innerWidth <= 768;
    this.isCompactView = window.innerWidth <= 1024;
    this._updatePinchZoomGuard();
  }

  
  private _updatePinchZoomGuard(): void {
    if (this.isMobileView) {
      this._enablePinchZoomGuard();
    } else {
      this._disablePinchZoomGuard();
    }
  }

  private _enablePinchZoomGuard(): void {
    if (this._pinchZoomGuardAttached || typeof document === 'undefined') return;
   
    const opts: AddEventListenerOptions = { passive: false, capture: true };
    document.addEventListener('gesturestart', this._onGestureZoom, opts);
    document.addEventListener('gesturechange', this._onGestureZoom, opts);
    document.addEventListener('gestureend', this._onGestureZoom, opts);
    document.addEventListener('touchstart', this._onMultiTouchZoom, opts);
    document.addEventListener('touchmove', this._onMultiTouchZoom, opts);
    document.addEventListener('wheel', this._onWheelZoom, opts);
    this._pinchZoomGuardAttached = true;
  }

  private _disablePinchZoomGuard(): void {
    if (!this._pinchZoomGuardAttached || typeof document === 'undefined') return;
    const opts: AddEventListenerOptions = { capture: true };
    document.removeEventListener('gesturestart', this._onGestureZoom, opts);
    document.removeEventListener('gesturechange', this._onGestureZoom, opts);
    document.removeEventListener('gestureend', this._onGestureZoom, opts);
    document.removeEventListener('touchstart', this._onMultiTouchZoom, opts);
    document.removeEventListener('touchmove', this._onMultiTouchZoom, opts);
    document.removeEventListener('wheel', this._onWheelZoom, opts);
    this._pinchZoomGuardAttached = false;
  }


  private _overlayZoomLockCount = 0;
  private readonly _onVisualViewportZoom = (): void => {
    this._applyOverlayZoomCompensation();
  };
  private _applyOverlayZoomCompensation(): void {
    if (typeof window === 'undefined') return;
    const container = document.querySelector(
      '.cdk-overlay-container',
    ) as HTMLElement | null;
    if (!container) return;
    const vv = window.visualViewport;
    if (!vv || vv.scale === 1) {
      container.style.transform = '';
      container.style.transformOrigin = '';
      return;
    }
    container.style.transformOrigin = '0 0';
    container.style.transform =
      `translate(${vv.offsetLeft}px, ${vv.offsetTop}px) scale(${1 / vv.scale})`;
  }
  private _lockOverlayAgainstZoom(): void {
    if (typeof window === 'undefined') return;
    this._overlayZoomLockCount++;
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', this._onVisualViewportZoom);
      vv.addEventListener('scroll', this._onVisualViewportZoom);
    }
    this._applyOverlayZoomCompensation();
  }
  private _unlockOverlayAgainstZoom(): void {
    if (typeof window === 'undefined') return;
    this._overlayZoomLockCount = Math.max(0, this._overlayZoomLockCount - 1);
    if (this._overlayZoomLockCount > 0) return;
    const vv = window.visualViewport;
    if (vv) {
      vv.removeEventListener('resize', this._onVisualViewportZoom);
      vv.removeEventListener('scroll', this._onVisualViewportZoom);
    }
    const container = document.querySelector(
      '.cdk-overlay-container',
    ) as HTMLElement | null;
    if (container) {
      container.style.transform = '';
      container.style.transformOrigin = '';
    }
  }

  toolbarIcon(item: { kind: "signature" | "comment" | "tag"; type?: EnumSignTagTypes }): string {
    if (item.kind === "signature") return "assets/signature-icon.png";
    if (item.kind === "comment") return "assets/comment-icon.png";
    switch (item.type) {
      case EnumSignTagTypes.INITIALS:
        return "assets/initials-icon.png";
      case EnumSignTagTypes.TEXT:
        return "assets/text-icon.png";
      case EnumSignTagTypes.DATE:
        return "assets/date-icon.png";
      case EnumSignTagTypes.CHECKBOX:
        return "assets/checklist-icon.png";
      case EnumSignTagTypes.STAMP:
        return "assets/stamp-icon.png";
      default:
        return "assets/signature-icon.png";
    }
  }

  downloadPDF() {
    this._pdfService.getCurrentDocumentAsBlob().then((data) => {
      if (!data) return;
      FileSaver.saveAs(data, this.fileName);
    });
  }

  openERSDDialog(ERSDDialogData: { requesterName: string, customerName: string, requesterMessage: string, EULAMessage: string, openDeclineConfirmationDialog: () => void }) {
    if (this._ersdDialogRef) {
      this._ersdDialogRef.close();
      this._ersdDialogRef = null;
    }

    const { requesterName, customerName, requesterMessage, EULAMessage, openDeclineConfirmationDialog } = ERSDDialogData;
    const dialogRef = this.dialog.open(ErsdAcceptanceComponent, {
      data: { requesterName, customerName, requesterMessage, EULAMessage, openDeclineConfirmationDialog },
      backdropClass: 'blur-backdrop',
      panelClass: 'rounded-dialog',
    });
    this._ersdDialogRef = dialogRef;

    const dialogSubscription: Subscription = dialogRef.componentInstance.confirmed.subscribe((isERSDAccepted: boolean) => {
      this.isERSDAccepted = isERSDAccepted;
    });

    dialogRef.afterClosed().subscribe(() => {
      dialogSubscription.unsubscribe();
      if (this._ersdDialogRef === dialogRef) {
        this._ersdDialogRef = null;
      }
    });
  }

  closeAdditionalInfo() {
    this.showAdditionalInfo = false;
    if (this.isSinglePage && this.isMobileView) this.drawerOpenedByUser = false;
    this.showScrollIndicator(true, true);
    this._cdr.detectChanges();
  }

  private reopenAdditionalInfoIfNeeded() {}

  private markPdfReady(): void {
    if (this.pdfReady) return;
    this.pdfReady = true;
    this._cancelSignPdfReadyTimers();
    this._cancelViewModePdfReadyWatch();
    if (this.isView && !this._viewModeScrollLayoutDone) {
      this._scheduleViewModeScrollLayout();
    }
    this._cdr.detectChanges();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          this._endPanelDelayPassed = true;
          if (this._canPlaceSignTags()) {
            if (!this.isView) {
              this._scheduleEndOfDocumentPrompt();
              this._startSignTagWatchdog();
            }
            this._placeAndBindSignTags();
          }
          this._cdr.detectChanges();
        }, 80);
      });
    });
  }

  private _getViewerContainer(): HTMLElement | null {
    for (const selector of ['#viewerContainer', '#outerContainer', '.pdfViewer']) {
      const el = document.querySelector(selector);
      if (el) return el as HTMLElement;
    }
    return null;
  }

  private _resetPdfScrollToStart(): void {
    const viewer = this._getViewerContainer();
    if (viewer) {
      viewer.scrollTop = 0;
    }
    try {
      this._pdfService.scrollPageIntoView(1, { top: '0%' });
    } catch {
      /* viewer not ready */
    }
    requestAnimationFrame(() => {
      const v = this._getViewerContainer();
      if (v) v.scrollTop = 0;
    });
  }

  private _isPdfContentLaidOut(): boolean {
    const pages = document.querySelectorAll('.pdfViewer .page');
    if (!pages.length) return false;
    const first = pages[0] as HTMLElement;
    const canvas = first.querySelector('canvas') as HTMLCanvasElement | null;
    return first.offsetHeight > 24 && (!!canvas && canvas.height > 0);
  }

  private _scheduleEndOfDocumentPrompt(): void {
    if (this.isView || !this.pdfReady || !this._endPanelDelayPassed) return;
    const viewer = this._getViewerContainer();
    if (!viewer) {
      setTimeout(() => this._scheduleEndOfDocumentPrompt(), 200);
      return;
    }
    this.checkScrollPosition(viewer);
    if (!this.isPdfFullyScrolled && !this.showAdditionalInfo) {
      this.showScrollIndicator(true, true);
    }
  }

  private _scheduleSignPdfReadyAfterRender(): void {
    if (this.pdfReady || !this._pdfLoaded) return;
    if (this._signPdfReadyTimer) clearTimeout(this._signPdfReadyTimer);

    const attemptReady = (): void => {
      if (this.pdfReady || !this._pdfLoaded) return;
      if (this._isPdfContentLaidOut()) {
        this._signPdfReadyTimer = null;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.markPdfReady());
        });
        return;
      }
      this._signPdfReadyTimer = setTimeout(attemptReady, 50);
    };

    this._signPdfReadyTimer = setTimeout(attemptReady, 0);
  }

  private _scheduleSignPdfReadyFallback(): void {
    this._cancelSignPdfReadyTimers();
    this._signPdfReadyFallbackTimer = setTimeout(() => {
      this._signPdfReadyFallbackTimer = null;
      if (!this.pdfReady && this._pdfLoaded) this.markPdfReady();
    }, 2500);
  }

  private _cancelSignPdfReadyTimers(): void {
    if (this._signPdfReadyTimer) {
      clearTimeout(this._signPdfReadyTimer);
      this._signPdfReadyTimer = null;
    }
    if (this._signPdfReadyFallbackTimer) {
      clearTimeout(this._signPdfReadyFallbackTimer);
      this._signPdfReadyFallbackTimer = null;
    }
  }


  private async _ensureAllPlacedPagesRendered(): Promise<void> {
    const pageIndexes = new Set<number>(this.signService.getPlacedSignaturePageIndexes());

    for (const pageIdx of this.signService.getRequiredTagPageIndexes(this._countedSignHereTagIds)) {
      pageIndexes.add(pageIdx);
    }

    Object.keys(this._requiredSignatureHereCount).forEach((pageIndex) => {
      pageIndexes.add(+pageIndex);
    });

    for (const target of this._signTargets) {
      if (target.page > 0) pageIndexes.add(target.page - 1);
    }

    const missingPages = new Set<number>();

    if (!pageIndexes.size && !this._countedSignHereTagIds.size) {
      this._submitMissingSignaturePages = [];
      return;
    }

    for (const pageIndex of pageIndexes) {
      const pageNumber = pageIndex + 1;
      try {
        this._pdfService.scrollPageIntoView(pageNumber);
      } catch { /* viewer not ready */ }

      for (let attempt = 0; attempt < 40; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 50));
        const currentTextLayer = document.querySelector(
          `.page[data-page-number="${pageNumber}"] .textLayer`,
        );
        if (currentTextLayer && currentTextLayer.children.length > 0) {
          this._placeAndBindSignTags();
          this.signService.restoreAllPlacedSignatures(this._viewContainerRef);
          await new Promise((resolve) => setTimeout(resolve, 100));
          break;
        }
      }

      if (this._countUnsignedSignaturePlaceholdersOnPage(pageNumber) > 0) {
        missingPages.add(pageNumber);
      }
    }

    this.signService.restoreAllPlacedSignatures(this._viewContainerRef);
    await new Promise((resolve) => setTimeout(resolve, 150));

    this._submitMissingSignaturePages = Array.from(missingPages.values()).sort(
      (a, b) => a - b,
    );
  }

  private _countUnsignedSignaturePlaceholdersOnPage(pageNumber: number): number {
    let count = 0;
    document
      .querySelectorAll(
        `.page[data-page-number="${pageNumber}"] .box-sign-here.sign:not(.sign-tag-comment-container):not(.comment-placeholder-container) img.img-sign-here`,
      )
      .forEach((img) => {
        const host = img.closest('.box-sign-here') as HTMLElement | null;
        if (!host) return;
        const tagId = host.getAttribute('data-kzn-tag-id') || '';
        if (tagId.startsWith('dp-')) return;
        if (tagId && this.signService.hasPlacedSignature(tagId)) return;
        if (this.signService.isSignHereHostFilled(host, tagId)) return;
        count++;
      });
    return count;
  }

  private _getRequiredSignHereOnPage(pageIndex: number): number {
    let count = 0;
    for (const tagId of this._countedSignHereTagIds) {
      if (tagId.startsWith('kc-') || tagId.startsWith('dp-')) continue;
      const host = document.querySelector(
        `[data-kzn-tag-id="${tagId}"]`,
      ) as HTMLElement | null;
      const rec = this.signService.getPlacedSignature(tagId);
      const hostPage = host?.getAttribute('page');
      const onPage = host
        ? (hostPage ?? '0') === String(pageIndex)
        : rec?.pageIndex === pageIndex;
      if (onPage) count++;
    }
    return count;
  }

  private _countFilledSignHereOnPage(pageIndex: number): number {
    let filled = 0;
    for (const tagId of this._countedSignHereTagIds) {
      if (tagId.startsWith('kc-') || tagId.startsWith('dp-')) continue;
      const host = document.querySelector(
        `[data-kzn-tag-id="${tagId}"]`,
      ) as HTMLElement | null;
      const registryRecord = this.signService.getPlacedSignature(tagId);
      const hostPage = host?.getAttribute('page');
      const onPage = host
        ? (hostPage ?? '0') === String(pageIndex)
        : registryRecord?.pageIndex === pageIndex;
      if (!onPage) continue;
      if (this.signService.isSignHereHostFilled(host, tagId)) filled++;
    }
    return filled;
  }

  private _countSignatureUploadsOnPage(
    pageIndex: number,
    signatures: Array<{ pageIndex: number; signImgStr?: string; type?: string }>,
  ): number {
    return signatures.filter(
      (signature) =>
        signature.pageIndex === pageIndex &&
        !!signature.signImgStr &&
        (signature.type === 'SIGNATURE' || !signature.type),
    ).length;
  }

  async signPDF() {
    let isInvalid: boolean = false;
    this._appService.updatePageLoaderVisibility(true, 'Submitting...');
    this._cdr.detectChanges();
    await new Promise(resolve => setTimeout(resolve, 0));
    
    await this._ensureAllPlacedPagesRendered();
    this._syncRequiredTagIdsBeforeSubmit();
    for (const [tagId] of this.signService.getPlacedSignatureEntries()) {
      await this.signService.refreshPlacedSignatureSnapshot(tagId, this._viewContainerRef);
    }
    const signatures = await this.signService.getPDFSignatures();

    if (!this.isReviewer) {
      if (this._submitMissingSignaturePages.length) {
        this._toastService.showError(
          this.signService.formatMissingSignaturePagesMessage(
            this._submitMissingSignaturePages,
          ),
        );
        this._appService.updatePageLoaderVisibility(false);
        return;
      }

      const tagValidation = this.signService.validateRequiredSignTagsFilled(
        this._countedSignHereTagIds,
        signatures,
      );
      if (!tagValidation.valid) {
        this._toastService.showError(
          tagValidation.errorMessage ?? Messages.MSG_ERROR_ALL_TAG_REQUIRED,
        );
        this._appService.updatePageLoaderVisibility(false);
        return;
      }

      const requiredCount = this.signService.getRequiredFieldsCount(this._countedSignHereTagIds);
      if (signatures.length < requiredCount) {
        this._toastService.showError(
          this.signService.buildRequiredSignatureMissingMessage(
            this._countedSignHereTagIds,
            signatures,
          ),
        );
        this._appService.updatePageLoaderVisibility(false);
        return;
      }

      const requiredSignatureCount = this.signService.countRequiredSignatureTags(
        this._countedSignHereTagIds,
      );
      const uploadedSignatureCount = this.signService.countUploadedSignatureFields(signatures);
      if (requiredSignatureCount > 0 && uploadedSignatureCount < requiredSignatureCount) {
        this._toastService.showError(
          this.signService.buildRequiredSignatureMissingMessage(
            this._countedSignHereTagIds,
            signatures,
          ),
        );
        this._appService.updatePageLoaderVisibility(false);
        return;
      }

      if (!this.signService.signTags.length) {
        const pagesWithSignatureHere = Object.keys(
          this._requiredSignatureHereCount
        );
        if (pagesWithSignatureHere.length) {
          const missingLegacySignaturePages: number[] = [];
          pagesWithSignatureHere.forEach((pageIndex) => {
            const required = this._getRequiredSignHereOnPage(+pageIndex);
            const filledTracked = this._countFilledSignHereOnPage(+pageIndex);
            const uploadedOnPage = this._countSignatureUploadsOnPage(+pageIndex, signatures);
            const registryOnPage = this.signService.countRegistrySignaturePlacements(+pageIndex);
            const satisfied =
              required === 0 ||
              Math.max(filledTracked, uploadedOnPage, registryOnPage) >= required;
            if (!satisfied) {
              isInvalid = true;
              missingLegacySignaturePages.push(+pageIndex + 1);
            }
          });
          if (isInvalid) {
            this._toastService.showError(
              this.signService.formatMissingSignaturePagesMessage(missingLegacySignaturePages),
            );
            this._appService.updatePageLoaderVisibility(false);
            return;
          }
        }
      }
    }

    const request: ISignUploadRequest = {
      signRequestId: this._fileRequestID,
      signerId: this._signerID,
      ERSD_Accepted: this.isERSDAccepted,
      signatures,
    };
    // console.log('signPDF request:', request);
    // return
    this._httpService
      .post(
        this._httpService.createEndpoint(Constants.API_URI_SIGN_UPLOAD),
        request
      )
      .subscribe(
        (response: any) => {
          this._appService.updatePageLoaderVisibility(false);
          this.statusMessage = {
            heading: this.t('thankYouHeading'),
            message: this.t('thankYouMessage'),
            isSuccess: true,
          };
          this.isSubmitted = true;
          this.isPdfFullyScrolled = false;
          if (!this.isView) {
            this._headerBeforeLoginService.setHeaderVisible(false);
            this._signToolbarService.setActive(false);
          }
          this._cdr.detectChanges();
        },
        (error) => {
          this._appService.updatePageLoaderVisibility(false);
          this._httpService.showHttpError(error);
        }
      );
  }

  async decline(declineReason: string) {
    this._appService.updatePageLoaderVisibility(true);
    const request: IDeclineSignRequest = {
      signRequestId: this._fileRequestID,
      signerId: this._signerID,
      declineReason,
      ERSD_Accepted: this.isERSDAccepted
    };

    this._httpService
      .post(
        this._httpService.createEndpoint(Constants.API_URI_DECLINE_SIGN_REQUEST),
        request
      )
      .subscribe(
        (response: any) => {
          this._appService.updatePageLoaderVisibility(false);
          this.statusMessage = {
            heading:
              "Thank you for sharing your valuable time",
            message:
              "This sign request has been successfully declined.",
            isSuccess: true,
          };
          this.isDeclined = true;
          this.isPdfFullyScrolled = false; 
          if (!this.isView) {
            this._headerBeforeLoginService.setHeaderVisible(false);
            this._signToolbarService.setActive(false);
          }
          this._cdr.detectChanges();
        },
        (error) => {
          this._appService.updatePageLoaderVisibility(false);
          this._httpService.showHttpError(error);
        }
      );
  }

  openDeclineConfirmationDialog(shouldFallbackToERSDDialog: boolean = false) {
    let wasCancelled = false;

    const dialogRef = this.dialog.open(DeclineSignRequestConfirmationComponent, {
      panelClass: 'rounded-dialog',
      backdropClass: 'blur-backdrop'
    });

    const dialogCancelSubscription: Subscription = dialogRef.componentInstance.cancelled.subscribe(() => {
      wasCancelled = true;
    });

    const dialogConfirmSubscription: Subscription = dialogRef.componentInstance.confirmed.subscribe((reasonForDecline: string) => {
      this.decline(reasonForDecline);
    });

    dialogRef.afterClosed().subscribe(() => {
      dialogCancelSubscription.unsubscribe();
      dialogConfirmSubscription.unsubscribe();
      if (wasCancelled && shouldFallbackToERSDDialog) {
        this.openERSDDialog(this._ERSDDialogData);
      }
    });
  }

  openSignDialog(
    isComment: boolean = false,
    shouldResetSelectedSpan: boolean = false,
    isResponsiveContainer: boolean = false
  ): void {
    if (shouldResetSelectedSpan) this._selectedSpan = undefined;
    const dialogRef = this.dialog.open(UploadSignComponent, {
      data: {
        isComment,
        isResponsiveContainer,
        signerName: this.signerName
      },
      panelClass: ['signature-dialog', 'rounded-dialog'],
      backdropClass: 'blur-backdrop'
    });

    dialogRef.afterOpened().subscribe(() => this._lockOverlayAgainstZoom());

    dialogRef.afterClosed().subscribe((data: any) => {
      this._unlockOverlayAgainstZoom();
        if (data?.imageBase64) {
        if (this._selectedSpan) {
          const isCommentSpan = this._selectedSpan.classList.contains('sign-tag-comment-container');
          const preserveStandardAspect =
            !isCommentSpan && this._selectedSpan.classList.contains('sign-here-standard');
          const currentW = this._selectedSpan.style.width || '';
          const currentH = this._selectedSpan.style.height || '';

          this._selectedSpan.innerHTML = '';
          const isMobileSign = typeof window !== 'undefined' && window.innerWidth <= 768;

          if (isCommentSpan) {
            this._selectedSpan.className = 'box-sign-here sign-tag-comment-container';
          } else if (isMobileSign) {
            /* Mobile: always use the compact sign-here-standard footprint so
               API tags and keyword tags render the same size after upload. */
            this._selectedSpan.className = 'box-sign-here sign-here-standard';
          } else {
            this._selectedSpan.className = preserveStandardAspect
              ? 'box-sign-here sign-here-standard'
              : 'box-sign-here';
          }

          if (isCommentSpan) {
            this._selectedSpan.style.width = `${Constants.DEFAULT_SIGN_COMMENT_WIDTH_PERCENTAGE}%`;
            this._selectedSpan.style.height = `${Constants.DEFAULT_SIGN_COMMENT_HEIGHT_PERCENTAGE}%`;
          } else if (isMobileSign) {
            this._selectedSpan.style.width = Constants.MOBILE_SIGN_IMAGE_WIDTH;
            this._selectedSpan.style.maxWidth = Constants.MOBILE_SIGN_IMAGE_MAX_WIDTH;
            this._selectedSpan.style.minWidth = Constants.MOBILE_SIGN_IMAGE_MIN_WIDTH;
            this._selectedSpan.style.removeProperty('height');
            this._selectedSpan.style.removeProperty('min-height');
          } else if (preserveStandardAspect) {
            if (!currentW || currentW === 'auto') {
              this._selectedSpan.style.width = Constants.DEFAULT_SIGN_IMAGE_WIDTH;
            }
            this._selectedSpan.style.minWidth = '120px';
            this._selectedSpan.style.removeProperty('height');
            this._selectedSpan.style.removeProperty('min-height');
          } else {
            if (!currentW || currentW === 'auto') {
              this._selectedSpan.style.width = Constants.DEFAULT_SIGN_IMAGE_WIDTH;
            }
            if (!currentH || currentH === 'auto') {
              this._selectedSpan.style.height = Constants.DEFAULT_SIGN_IMAGE_HEIGHT;
            }
            this._selectedSpan.style.minWidth = '120px';
            this._selectedSpan.style.minHeight = '64px';
          }
          const isDragPlaceholder = this._selectedSpan?.getAttribute('data-drag-placeholder') === 'true';
          const allowMove = isDragPlaceholder;
          const pageIndexAtPlacement = this._pageNumber ?? 0;
          let tagId = this._selectedSpan.getAttribute('data-kzn-tag-id') || '';
          if (!tagId) {
            tagId = `sh-${pageIndexAtPlacement}-${this.signService.generateUUID()}`;
            this._selectedSpan.setAttribute('data-kzn-tag-id', tagId);
          }
          this.signService.addSignImage(
            data.imageBase64,
            "0",
            "0",
            pageIndexAtPlacement,
            this._viewContainerRef,
            allowMove,
            data.isCommentOnly,
            data.signComment,
            this._selectedSpan,
            data.comment,
            data.signatureId,
            true,
            !isDragPlaceholder
          );
          const hostSpan = this._selectedSpan;
          this.signService.registerPlacedSignature(tagId, {
            imageBase64: data.imageBase64,
            isCommentOnly: !!data.isCommentOnly,
            signComment: data.signComment,
            comment: data.comment,
            signatureId: data.signatureId,
            isResponsiveContainer: true,
            isDraggable: allowMove,
            disableDelete: !isDragPlaceholder,
            pageIndex: pageIndexAtPlacement,
            hostGeometry: {
              left: hostSpan.style.left || '',
              top: hostSpan.style.top || '',
              width: hostSpan.style.width || '',
              height: hostSpan.style.height || '',
              className: hostSpan.className || 'box-sign-here',
              page: hostSpan.getAttribute('page') || pageIndexAtPlacement.toString(),
            },
          });
          void this.signService.refreshPlacedSignatureSnapshot(tagId, this._viewContainerRef);
          this.signService.removeSignHerePlaceholderImages(hostSpan);
          this.signService.clearFilledSignHerePlaceholders();
          this._updateNextButton();
          this.updateKapybaraSuggestedQuestions();
          this._selectedSpan = undefined;
        } else if (data.isCommentOnly && typeof window !== 'undefined' && window.innerWidth <= 768) {
          const textLayers = document.getElementsByClassName('textLayer');
          const textLayer = textLayers.length ? textLayers[0] as HTMLElement : null;
          if (textLayer) {
            const span = document.createElement('span');
            span.className = 'box-sign-here sign-tag-comment-container';
            span.style.position = 'absolute';
            span.style.left = '5%';
            span.style.top = '5%';
            span.style.width = `${Constants.DEFAULT_SIGN_COMMENT_WIDTH_PERCENTAGE}%`;
            span.style.height = `${Constants.DEFAULT_SIGN_COMMENT_HEIGHT_PERCENTAGE}%`;
            textLayer.appendChild(span);
            this.signService.addSignImage(
              data.imageBase64,
              '0',
              '0',
              0,
              this._viewContainerRef,
              true,
              data.isCommentOnly,
              data.signComment,
              span,
              data.comment,
              data.signatureId,
              true
            );
          }
        } else {
          this.signService.addSign(
            data.imageBase64,
            this._viewContainerRef,
            data.isCommentOnly,
            data.signComment,
            data.comment,
            data.signatureId,
            data.isResponsiveContainer
          );
        }
      }
    });
  }

   

  nextSignTagHandler() {
    if (this.tagDragHidesNext) {
      return;
    }

    const mySeq = ++this._nextHighlightSeq;

   
    if (this._signTargets.length > 0) {
    
      if (this._signTargetCursor === -1) {
        const currentPage = this._safeGetCurrentPage();
        const firstBelow = this._signTargets.findIndex((t) => t.page > currentPage);
        this._signTargetCursor = firstBelow >= 0
          ? firstBelow
          : 0; // no target below — wrap to the first
      } else {
        this._signTargetCursor = (this._signTargetCursor + 1) % this._signTargets.length;
      }
      const target = this._signTargets[this._signTargetCursor];

      // Clear any existing highlight before scrolling.
      document.querySelectorAll('.box-sign-here.sign.current-sign')
        .forEach((el) => el.classList.remove('current-sign'));

  
      this._positionNextButtonForTarget(target);

   
      this._smoothScrollToTarget(target);
      try {
        this._pdfService.scrollPageIntoView(target.page, { top: `${Math.max(0, Math.min(95, target.y - 5))}%` });
      } catch {
      }

      this._waitForTextLayer(target.page, 35, () => {
        this._placeAndBindSignTags();
        this._waitForAndHighlightTarget(target, 0, mySeq);
      });
      return;
    }

   
    this._placeAndBindSignTags();

   
    const signElements = document.querySelectorAll('.box-sign-here.sign');
    if (signElements.length === 0) return;

    let currentIndex = -1;
    signElements.forEach((el, index) => {
      if (el.classList.contains('current-sign')) {
        currentIndex = index;
        el.classList.remove('current-sign');
      }
    });

    const nextIndex = currentIndex + 1 < signElements.length ? currentIndex + 1 : 0;
    const nextSignElement = signElements[nextIndex] as HTMLElement;
    nextSignElement.classList.add('current-sign');
    nextSignElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

   
    this._positionNextButtonFor(nextSignElement);
  }


  private async _buildSignTargets(): Promise<void> {
    if (this._signTargetsBuilding) return;
    this._signTargetsBuilding = true;

    const sortFn = (
      a: { page: number; y: number; occurrenceInPage?: number },
      b: { page: number; y: number; occurrenceInPage?: number },
    ): number => (a.page - b.page) || (a.y - b.y) || ((a.occurrenceInPage ?? 0) - (b.occurrenceInPage ?? 0));

    const isSigner = this.signerRole?.toLowerCase() === EnumSignerRoleType.SIGNER.toLowerCase();

    const pathATargets: Array<{ page: number; y: number; kind: 'A'; signTagsIndex: number; }> = [];
    (this.signService.signTags ?? []).forEach((tag, idx) => {
      if (tag.Type !== EnumSignTagTypes.SIGNATURE) return;
      pathATargets.push({ page: tag.Page, y: Number(tag.YLocation) || 0, kind: 'A', signTagsIndex: idx });
    });

   
    this._signTargets = [...pathATargets].sort(sortFn);

    this._signTargetsBuilding = false;

    if (pathATargets.length > 0 && !this.isView) {
      setTimeout(() => this._startSignTagWatchdog(), 200);
    }

    const sigTag = (this._signatureTag || '').toLowerCase();
    const altTag = (this._signatureDynamicTag || '').toLowerCase();
    if (!isSigner || (!sigTag && !altTag) || this._pagesCount <= 0) {
      return;
    }

    setTimeout(() => void this._appendPathBSignTargets(pathATargets, sortFn), this._pagesCount > 15 ? 5000 : 800);
  }

  private async _appendPathBSignTargets(
    pathATargets: Array<{ page: number; y: number; kind: 'A'; signTagsIndex: number; }>,
    sortFn: (
      a: { page: number; y: number; occurrenceInPage?: number },
      b: { page: number; y: number; occurrenceInPage?: number },
    ) => number,
  ): Promise<void> {
    if (this._signTargetsBuilding) return;
    this._signTargetsBuilding = true;

    const sigTag = (this._signatureTag || '').toLowerCase();
    const altTag = (this._signatureDynamicTag || '').toLowerCase();
    const countOccurrences = (haystack: string, needle: string): number => {
      if (!needle) return 0;
      let count = 0;
      let i = 0;
      while ((i = haystack.indexOf(needle, i)) !== -1) { count++; i += needle.length; }
      return count;
    };

    const pathBTargets: Array<{ page: number; y: number; kind: 'B'; occurrenceInPage: number; }> = [];
    for (let p = 1; p <= this._pagesCount; p++) {
      if (p > 1 && p % 4 === 0) {
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
      try {
        const raw = (await this._pdfService.getPageAsText(p)) || '';
        const text = raw.toLowerCase();
        const total = countOccurrences(text, sigTag) + (altTag !== sigTag ? countOccurrences(text, altTag) : 0);
        for (let n = 0; n < total; n++) {
          pathBTargets.push({ page: p, y: 50, kind: 'B', occurrenceInPage: n });
        }
      } catch {
      }
    }
    if (pathBTargets.length > 0) {
      this._signTargets = [...pathATargets, ...pathBTargets].sort(sortFn);
      this._startSignTagWatchdog();
    }

    this._signTargetsBuilding = false;
  }


  private _waitForAndHighlightTarget(
    target: { page: number; y: number; kind: 'A' | 'B'; signTagsIndex?: number; occurrenceInPage?: number; },
    retries: number,
    seq: number,
  ): void {
    if (seq !== this._nextHighlightSeq) return;
    const maxRetries = 120;
    const tryHighlight = (): boolean => {
      if (retries % 2 === 0) this._placeAndBindSignTags();
      const el = this._resolveTargetSpan(target);
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 6 && rect.height <= 4) return false;
      el.classList.add('current-sign');
      try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch {}
      this._positionNextButtonFor(el);
      return true;
    };

    if (tryHighlight()) return;
    if (retries >= maxRetries) return;
    setTimeout(() => this._waitForAndHighlightTarget(target, retries + 1, seq), 40);
  }


  private _smoothScrollToTarget(target: { page: number; y: number; }): void {
    const pageEl = document.querySelector(`.page[data-page-number="${target.page}"]`) as HTMLElement | null;
    if (!pageEl) return;
    const scrollContainer = (document.getElementById('viewerContainer') as HTMLElement | null)
      ?? (document.getElementById('outerContainer') as HTMLElement | null);
    if (!scrollContainer) return;

    const pageRect = pageEl.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();
    if (pageRect.height === 0 || containerRect.height === 0) return;

    const targetYInPage = pageRect.height * (Math.max(0, Math.min(100, target.y)) / 100);
    const desiredViewportY = containerRect.top + (containerRect.height * 0.3);
    const currentViewportY = pageRect.top + targetYInPage;
    const scrollDelta = currentViewportY - desiredViewportY;
    if (Math.abs(scrollDelta) < 4) return; // already roughly in position

    try {
      scrollContainer.scrollBy({ top: scrollDelta, behavior: 'smooth' });
    } catch {
      // older browsers: fall back to instant scroll
      scrollContainer.scrollTop += scrollDelta;
    }
  }

  /** Best-effort current page from the PDF viewer; falls back to 1. */
  private _safeGetCurrentPage(): number {
    try {
      const p = (this._pdfService as any)?.getCurrentPage?.();
      if (typeof p === 'number' && p >= 1) return p;
    } catch {}
    return 1;
  }


  private _positionNextButtonForTarget(target: { page: number; y: number; }): void {
    const nextButton = document.getElementById('btn__next') as HTMLElement | null;
    if (!nextButton || this.tagDragHidesNext) return;
    const pageEl = document.querySelector(`.page[data-page-number="${target.page}"]`) as HTMLElement | null;
    if (!pageEl) return;
    const offsetParent = (nextButton.offsetParent as HTMLElement | null)
      ?? (document.getElementsByClassName('pdfViewer')[0] as HTMLElement | null);
    if (!offsetParent) return;

    const pageRect = pageEl.getBoundingClientRect();
    const parentRect = offsetParent.getBoundingClientRect();
    if (pageRect.height === 0 || parentRect.height === 0) return;

    const yFraction = Math.max(0, Math.min(100, target.y)) / 100;
    const top = (pageRect.top - parentRect.top) + (pageRect.height * yFraction);
    nextButton.style.top = `${top}px`;
  }


  private _resolveTargetSpan(target: { page: number; kind: 'A' | 'B'; signTagsIndex?: number; occurrenceInPage?: number; }): HTMLElement | null {
    if (target.kind === 'A' && target.signTagsIndex != null) {
      const el = document.querySelector(`[data-kzn-tag-id="pt-${target.signTagsIndex}"]`) as HTMLElement | null;
      if (el && el.isConnected) return el;
      return null;
    }
    // Path B: look within the specific page wrapper.
    const pageEl = document.querySelector(`.page[data-page-number="${target.page}"]`) as HTMLElement | null;
    if (!pageEl) return null;
    const spans = pageEl.querySelectorAll('.box-sign-here.sign');
    const idx = Math.min(target.occurrenceInPage ?? 0, Math.max(0, spans.length - 1));
    return (spans.item(idx) as HTMLElement | null) ?? null;
  }


  private _positionNextButtonFor(el: HTMLElement, _pageNumberOneBased?: number): void {
    const nextButton = document.getElementById('btn__next') as HTMLElement | null;
    if (!nextButton || this.tagDragHidesNext) return;
    const offsetParent = (nextButton.offsetParent as HTMLElement | null)
      ?? (document.getElementsByClassName('pdfViewer')[0] as HTMLElement | null);
    if (!offsetParent) return;

    const elRect = el.getBoundingClientRect();
    const parentRect = offsetParent.getBoundingClientRect();
    if (elRect.height === 0 || parentRect.height === 0) return;

    const top = (elRect.top - parentRect.top) + (elRect.height / 2);
    nextButton.style.top = `${top}px`;
  }

  onAnnotationLayerRendered(event: AnnotationLayerRenderedEvent | any): void {
    if (this.isView) {
      if (!this._viewModeScrollLayoutDone) {
        this._scheduleViewModeScrollLayout();
      }
      if (this._canPlaceSignTags() && event?.pageNumber) {
        this._scheduleSignTagPlacementForPage(event.pageNumber, 80);
      }
      this._fixOverflowClipping();
      if (!this.pdfReady) {
        this.markPdfReady();
      }
      return;
    }

    if (this.isSigned) return;

    this._ensureSignChromeReady();

    if (event?.pageNumber) {
      this._scheduleSignTagPlacementForPage(event.pageNumber, 80);
    }

    if (!this._signSetupDone && this._pdfLoaded) {
      this._scheduleSignTagPlacement(50);
    }

    if (!this._signSetupDone && event.pageNumber === this._pagesCount) {
      this._signSetupDone = true;
      this.pageViewMode = "multiple";

      setTimeout(() => {
        this._placeAndBindSignTags();
        this._captureFitZoomFloor();
        if (!this.pdfReady) {
          this.markPdfReady();
        }
        const viewerContainer = document.querySelector('#viewerContainer') ||
                               document.querySelector('#outerContainer') ||
                               document.querySelector('.pdfViewer');
        if (viewerContainer) {
          this.checkScrollPosition(viewerContainer as HTMLElement);
        }
        this._fixOverflowClipping();
      }, 0);
      return;
    }

   
    if (this._signSetupDone) {
      this._scheduleSignTagPlacement(50);
    }
  }

  private _ensureSignChromeReady(): void {
    if (this.isView || this.isSigned || this._signChromeReady) return;
    this._signChromeReady = true;

    const outerContainer = document.getElementById('outerContainer');
    const footer = document.querySelector('.footer-sign');
    if (outerContainer) {
      outerContainer.style.position = 'fixed';
      outerContainer.style.height = `calc(100% - ${footer?.clientHeight || 0}px)`;
    }

    this._attachSignLayoutObserver();
  }

  private _startSignTagWatchdog(): void {
    if (this.isView || this.isSigned || this.isReviewer) return;
    if (this._countVisibleSignTags() >= this._expectedVisibleSignTagCount()) return;
    this._stopSignTagWatchdog();
    const seq = ++this._signTagWatchSeq;
    [150, 400, 800, 1400, 2200, 3200, 4500, 6500, 9000, 12000, 15000, 20000].forEach((ms) => {
      this._signTagWatchTimers.push(
        setTimeout(() => {
          if (seq !== this._signTagWatchSeq || this.isView || this.isSigned) return;
          this._ensureSignTagsVisible();
        }, ms),
      );
    });
  }

  private _stopSignTagWatchdog(): void {
    this._signTagWatchTimers.forEach((t) => clearTimeout(t));
    this._signTagWatchTimers = [];
    this._signTagWatchSeq++;
  }

  private _countVisibleSignTags(): number {
    const seen = new Set<HTMLElement>();
    let count = 0;
    const addIfVisible = (el: HTMLElement | null | undefined): void => {
      if (!el || seen.has(el)) return;
      const rect = el.getBoundingClientRect();
      if (rect.width > 6 && rect.height > 4) {
        seen.add(el);
        count++;
      }
    };

    document.querySelectorAll('.box-sign-here.sign img.img-sign-here').forEach((img) => {
      addIfVisible(img.closest('.box-sign-here') as HTMLElement);
    });
    document.querySelectorAll('.box-sign-here.sign .cdk-drag').forEach((el) => {
      addIfVisible(el.closest('.box-sign-here') as HTMLElement);
    });
    document.querySelectorAll('.sign-tag-element-container[data-kzn-tag-id]').forEach((node) => {
      addIfVisible(node as HTMLElement);
    });
    document.querySelectorAll('[data-kzn-tag-id^="pt-"]').forEach((node) => {
      const host = node as HTMLElement;
      if (host.classList.contains('sign-tag-element-container') || host.classList.contains('box-sign-here')) {
        addIfVisible(host);
      }
    });
    return count;
  }

  private _countVisibleSignHerePlaceholders(): number {
    return this._countVisibleSignTags();
  }

  private _waitForTextLayer(pageNumber: number, attemptsLeft: number, cb: () => void): void {
    const layer = document.querySelector(
      `.page[data-page-number="${pageNumber}"] .textLayer`,
    ) as HTMLElement | null;
    if (layer && layer.children.length > 0) {
      cb();
      return;
    }
    if (attemptsLeft <= 0) {
      cb();
      return;
    }
    setTimeout(() => this._waitForTextLayer(pageNumber, attemptsLeft - 1, cb), 80);
  }

  private _ensureSignTagsVisible(): void {
    if (this.isReviewer) return;
    if (this._countVisibleSignTags() >= this._expectedVisibleSignTagCount()) {
      this._stopSignTagWatchdog();
      return;
    }

    void this._restoreSignTagsAfterLayoutChange();
    if (this._countVisibleSignTags() >= this._expectedVisibleSignTagCount()) {
      this._stopSignTagWatchdog();
    }
  }

 
  private _attachSignLayoutObserver(): void {
    if (this.isView || this._signLayoutObserver) return;
    const viewer =
      (document.querySelector('.pdfViewer') as HTMLElement | null) ||
      (document.querySelector('#viewerContainer') as HTMLElement | null);
    if (!viewer) return;

    this._signLayoutObserver = new MutationObserver(() => {
      if (this.isView || this.isSigned) return;
      this._scheduleSignTagsLayoutRestore(180);
    });

    this._signLayoutObserver.observe(viewer, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  private _detachSignLayoutObserver(): void {
    if (this._signLayoutObserver) {
      this._signLayoutObserver.disconnect();
      this._signLayoutObserver = null;
    }
  }

  
  onZoomFactorChange(factor: number): void {
    if (this.isView || typeof factor !== 'number' || factor <= 0) return;

    if (this._pdfLoaded) {
      this._scheduleSignTagsLayoutRestoreBurst();
    }

    if (this._fitZoomFactor == null) return;
    if (factor < this._fitZoomFactor - 0.01) {
      this._snapZoomToFit();
    }
  }


  private _scheduleSignTagsLayoutRestoreBurst(): void {
    if (this.isView || !this._pdfLoaded) return;
    this._clearZoomReplacementBurst();
    [0, 150, 400, 800, 1400, 2200, 3200, 4500, 6000, 8000].forEach((ms) => {
      this._zoomBurstTimers.push(
        setTimeout(() => this._scheduleSignTagsLayoutRestore(0), ms),
      );
    });
  }

  private _scheduleSignTagsLayoutRestore(delayMs: number = 120): void {
    if (this.isView || this.isSigned || !this._canPlaceSignTags() || !this._pdfLoaded) return;
    if (this._tagLayoutRestoreTimer) clearTimeout(this._tagLayoutRestoreTimer);
    this._tagLayoutRestoreTimer = setTimeout(() => {
      this._tagLayoutRestoreTimer = null;
      void this._restoreSignTagsAfterLayoutChange();
    }, delayMs);
  }

  private _waitForPageTagMount(pageNumber: number): Promise<void> {
    return new Promise((resolve) => {
      this._waitForTextLayer(pageNumber, 24, resolve);
    });
  }

  private async _restoreSignTagsAfterLayoutChange(): Promise<void> {
    if (this.isView || this.isSigned || !this._canPlaceSignTags() || !this._pdfLoaded) return;
    if (this._tagLayoutRestoreInFlight) {
      this._tagLayoutRestoreQueued = true;
      return;
    }
    this._tagLayoutRestoreInFlight = true;
    try {
      const pageNumbers = new Set<number>();
      for (const tag of this.signService.signTags) {
        if (tag.Page > 0) pageNumbers.add(tag.Page);
      }
      for (const pageIdx of this.signService.getPlacedSignaturePageIndexes()) {
        pageNumbers.add(pageIdx + 1);
      }
      for (const target of this._signTargets) {
        if (target.page > 0) pageNumbers.add(target.page);
      }
      document.querySelectorAll('.pdfViewer .page[data-page-number]').forEach((node) => {
        const pageNumber = parseInt(node.getAttribute('data-page-number') || '0', 10);
        if (pageNumber > 0) pageNumbers.add(pageNumber);
      });

      for (const pageNumber of pageNumbers) {
        await this._waitForPageTagMount(pageNumber);
      }

      this._placeAndBindSignTags();
      this.signService.recreateOrphanedPlacements(this._viewContainerRef);
      this.signService.restoreAllPlacedSignatures(this._viewContainerRef);
      this._fixOverflowClipping();
      this.signService.syncRequiredFieldsCount(this._countedSignHereTagIds);
      this.updateKapybaraSuggestedQuestions();

      if (
        this._countVisibleSignTags() < this._expectedVisibleSignTagCount() &&
        (this.signService.signTags.length > 0 || this._signTargets.length > 0)
      ) {
        this._startSignTagWatchdog();
      }
    } finally {
      this._tagLayoutRestoreInFlight = false;
      if (this._tagLayoutRestoreQueued) {
        this._tagLayoutRestoreQueued = false;
        this._scheduleSignTagsLayoutRestore(80);
      }
    }
  }

  private _syncRequiredTagIdsBeforeSubmit(): void {
    if (this.isReviewer) return;

    if (this.signService.signTags.length) {
      this.signService.signTags.forEach((tag, index) => {
        if (tag.Type === EnumSignTagTypes.COMMENT) return;
        this._countedSignHereTagIds.add(`pt-${index}`);
      });
    }

    document.querySelectorAll<HTMLElement>('[data-kzn-tag-id]').forEach((host) => {
      const tagId = host.getAttribute('data-kzn-tag-id') || '';
      if (!tagId || tagId.startsWith('dp-') || tagId.startsWith('kc-')) return;
      if (
        host.classList.contains('sign-tag-comment-container') ||
        host.classList.contains('comment-placeholder-container')
      ) {
        return;
      }
      if (host.classList.contains('box-sign-here') && host.classList.contains('sign')) {
        this._countedSignHereTagIds.add(tagId);
        return;
      }
      if (host.classList.contains('sign-tag-element-container')) {
        this._countedSignHereTagIds.add(tagId);
      }
    });

    this.signService.syncRequiredFieldsCount(this._countedSignHereTagIds);
  }

  private _expectedVisibleSignTagCount(): number {
    if (this.isReviewer) return 0;
    return Math.max(
      this.signService.getRequiredFieldsCount(this._countedSignHereTagIds),
      1,
    );
  }

  private _scheduleZoomReplacementBurst(): void {
    this._scheduleSignTagsLayoutRestoreBurst();
  }

  private _clearZoomReplacementBurst(): void {
    this._zoomBurstTimers.forEach((t) => clearTimeout(t));
    this._zoomBurstTimers = [];
  }

  private _getPdfViewer(): any {
    const app = this._pdfNotification?.onPDFJSInitSignal?.();
    return app?.pdfViewer ?? null;
  }


  private _captureFitZoomFloor(attemptsLeft: number = 8): void {
    if (this.isView) return;
    const viewer = this._getPdfViewer();
    const scale = viewer?.currentScale;
    if (viewer && typeof scale === 'number' && scale > 0) {
      this._fitZoomFactor = scale;
      this._zoomFloorWidth = window.innerWidth || 1;
      try {
        viewer.minZoom = scale;
      } catch {
      }
      return;
    }
    if (attemptsLeft > 0) {
      setTimeout(() => this._captureFitZoomFloor(attemptsLeft - 1), 250);
    }
  }

 
  private _snapZoomToFit(): void {
    if (this._zoomClampScheduled) return;
    this._zoomClampScheduled = true;
    setTimeout(() => {
      this._zoomClampScheduled = false;
      const viewer = this._getPdfViewer();
      if (viewer) {
        viewer.currentScaleValue = 'auto';
      }
    }, 60);
  }

 
  private _rescaleZoomFloorForWidth(): void {
    if (this.isView || this._fitZoomFactor == null) return;
    const newWidth = window.innerWidth || 1;
    if (this._zoomFloorWidth && newWidth !== this._zoomFloorWidth) {
      this._fitZoomFactor =
        this._fitZoomFactor * (newWidth / this._zoomFloorWidth);
      this._zoomFloorWidth = newWidth;
      const viewer = this._getPdfViewer();
      if (viewer) {
        try {
          viewer.minZoom = this._fitZoomFactor;
        } catch {
          /* ignore */
        }
      }
    }
  }

  private _placeAndBindSignTags(): void {
    const { signHereElements, commentHereElements } =
      this.signService.generateSignHereElements(
        this._signatureTag,
        this._order,
        this._viewContainerRef,
        this.signerRole,
        this._signatureDynamicTag,
      );

   
    const apiHasSignature = this.signService.signTags?.some(
      (t) => t.Type === EnumSignTagTypes.SIGNATURE,
    ) ?? false;
    const realSignHereElements = signHereElements.filter((el) => {
      const id = el.getAttribute('data-kzn-tag-id') || '';
      return !id.startsWith('dp-') && el.getAttribute('data-drag-placeholder') !== 'true';
    });
    if (!this.isReviewer && (realSignHereElements.length > 0 || apiHasSignature)) {
      this._signHereDetectedEver = true;
    }
    this.hasSignHereElements =
      !this.isReviewer && (this._signHereDetectedEver || realSignHereElements.length > 0);
    this._cdr.detectChanges();

    const addEvent = (element: HTMLElement, isComment: boolean = false) => {
      if (this.isReviewer && !isComment) {
        return;
      }
      const spanPageNumber = element.getAttribute("page") ?? "0";
      let tagId = element.getAttribute('data-kzn-tag-id') || '';

      if (!tagId && !isComment) {
        tagId = `sh-${spanPageNumber}-${this.signService.generateUUID()}`;
        element.setAttribute('data-kzn-tag-id', tagId);
      }

      if (tagId && !tagId.startsWith('dp-') && !this._countedSignHereTagIds.has(tagId)) {
        this._countedSignHereTagIds.add(tagId);
        const currentCount =
          this._requiredSignatureHereCount[spanPageNumber] ?? 0;
        this._requiredSignatureHereCount[spanPageNumber] = isComment
          ? currentCount
          : currentCount + 1;
      }

     
      if (element.getAttribute('data-kzn-signhere-bound') === 'true') return;
      element.setAttribute('data-kzn-signhere-bound', 'true');

      const onClick = (ev: Event) => {
        const target = ev.target as HTMLElement | null;

        if (target?.closest('.action-handles-container')) return;
        if (target?.closest('.resizer')) return;

        this._selectedSpan = element;
        this._pageNumber = +spanPageNumber;
        const isResponsiveContainer = !isComment && !element.classList.contains('sign-tag-comment-container');
        this.openSignDialog(isComment, false, isResponsiveContainer);
      };
      element.addEventListener("click", onClick);
    };

    signHereElements.forEach((span: HTMLElement) => addEvent(span));
    commentHereElements.forEach((span: HTMLElement) => addEvent(span, true));

   
    this.signService.recreateOrphanedPlacements(this._viewContainerRef);

    this._dedupeSignTags();
    this.signService.clearFilledSignHerePlaceholders();
    if (this.isReviewer) {
      this.signService.suppressSignHereForNonSignerRole(this.signerRole);
    }

    this._updateNextButton();
    this.signService.syncRequiredFieldsCount(this._countedSignHereTagIds);
    this.updateKapybaraSuggestedQuestions();
    this._markCompletedCustomTagFields();

    this._fixOverflowClipping();

    if (this._countVisibleSignHerePlaceholders() > 0) {
      this._stopSignTagWatchdog();
    } else if (this.pdfReady && (apiHasSignature || this._signTargets.length > 0)) {
      this._startSignTagWatchdog();
    }
  }

 
  private _dedupeSignTags(): void {
    if (typeof document === 'undefined') return;

    
    const byId = new Map<string, HTMLElement[]>();
    document
      .querySelectorAll<HTMLElement>('.box-sign-here[data-kzn-tag-id]')
      .forEach((el) => {
        const id = el.getAttribute('data-kzn-tag-id') || '';
        if (!id) return;
        const list = byId.get(id) ?? [];
        list.push(el);
        byId.set(id, list);
      });
    byId.forEach((els) => {
      if (els.length < 2) return;
      const withDrag = els.filter((e) => e.querySelector('.cdk-drag'));
      const keep = withDrag[0] ?? els[0];
      els.forEach((e) => {
        if (e !== keep) e.remove();
      });
    });

    const customById = new Map<string, HTMLElement[]>();
    document
      .querySelectorAll<HTMLElement>('.sign-tag-element-container[data-kzn-tag-id]')
      .forEach((el) => {
        const id = el.getAttribute('data-kzn-tag-id') || '';
        if (!id) return;
        const list = customById.get(id) ?? [];
        list.push(el);
        customById.set(id, list);
      });
    customById.forEach((els) => {
      if (els.length < 2) return;
      const keep = els[0];
      els.slice(1).forEach((e) => e.remove());
    });

    // 2) Multiple placed signatures inside a single host.
    document.querySelectorAll<HTMLElement>('.box-sign-here').forEach((host) => {
      const drags = Array.from(host.children).filter((c) =>
        (c as HTMLElement).classList.contains('cdk-drag'),
      );
      drags.slice(1).forEach((d) => d.remove());
    });
  }


  private _markCompletedCustomTagFields(): void {
    document.querySelectorAll<HTMLElement>('.sign-tag-element-container[data-kzn-tag-id]').forEach((host) => {
      const hasValue =
        !!host.querySelector('[data-value-available="true"]') ||
        !!host.querySelector('img[data-value-available="true"]');
      if (hasValue) {
        host.classList.add('sign-tag-field-completed');
      }
    });
  }

  private _resolveHeaderImageSrc(headerImageRaw: unknown): string | null {
    const headerImage = typeof headerImageRaw === 'string' ? headerImageRaw.trim() : '';
    if (!headerImage || headerImage.toLowerCase() === 'null') return null;
    const lower = headerImage.toLowerCase();
    if (headerImage.startsWith('data:')) return headerImage;
    if (lower.startsWith('http://') || lower.startsWith('https://') || headerImage.startsWith('/')) {
      return headerImage;
    }

    const isPng = headerImage.startsWith('iVBORw0K');
    const isJpg = headerImage.startsWith('/9j/');
    const isGif = headerImage.startsWith('R0lGOD');
    const isWebp = headerImage.startsWith('UklGR');
    const isSvg = headerImage.startsWith('PHN2Zy');
    const mime = isPng
      ? 'image/png'
      : isJpg
        ? 'image/jpeg'
        : isGif
          ? 'image/gif'
          : isWebp
            ? 'image/webp'
            : isSvg
              ? 'image/svg+xml'
              : 'image/png';
    return `data:${mime};base64,${headerImage}`;
  }

  private _updateNextButton(): void {
    const nextEl = document.getElementById('btn__next') as HTMLElement | null;
    if (!nextEl) return;

    if (this.tagDragHidesNext) {
      nextEl.classList.remove('show');
      nextEl.style.display = 'none';
      nextEl.style.visibility = 'hidden';
      nextEl.style.pointerEvents = 'none';
      return;
    }

    const isReviewer = this.signerRole?.toLowerCase() === EnumSignerRoleType.REVIEWER.toLowerCase();
    if (this.isView || isReviewer || !this.hasSignHereElements) {
      nextEl.classList.remove('show');
      nextEl.style.removeProperty('display');
      return;
    }

    if (!this._signNextBtnAttached) {
      const pdfViewer = document.getElementsByClassName('pdfViewer')[0];
      if (pdfViewer) {
        pdfViewer.appendChild(nextEl);
        this._signNextBtnAttached = true;
      }
    }

    const textLayerRect = document.getElementsByClassName('textLayer')[0]
      ?.getBoundingClientRect();
    if (textLayerRect && textLayerRect.width > 0) {
      nextEl.style.left = `calc(${textLayerRect.left}px - 5.5rem - 20px)`;
    } else if (!nextEl.style.left) {
      nextEl.style.left = '20px';
    }

    if (!nextEl.style.top) {
      nextEl.style.top = '20px';
    }

    nextEl.style.removeProperty('display');
    nextEl.classList.add('show');
  }

  private activeClickHandler?: EventListener;
  private cursorEl?: HTMLElement;
  private lastClickListeners: (() => void)[] = [];

  activateClickMode(type: EnumSignTagTypes, dimensions: { height: string; width: string }) {
    const viewerEl = document.getElementById('viewer');
    if (!viewerEl) return;

    if (this.cursorEl?.parentElement) {
      this.cursorEl.parentElement.removeChild(this.cursorEl);
    }

    if (this.activeClickHandler) {
      viewerEl.removeEventListener('mousemove', this.activeClickHandler);
    }

    this.lastClickListeners.forEach(remove => remove());
    this.lastClickListeners = [];

    if (!this.isMobileView) {
      const cursorEl = document.createElement('span');
      cursorEl.style.position = 'absolute';
      cursorEl.style.zIndex = '9999';
      cursorEl.style.pointerEvents = 'none';
      cursorEl.style.background = '#ffffffcc';
      cursorEl.style.padding = '4px 8px';
      cursorEl.style.border = '1px solid #ccc';
      cursorEl.style.borderRadius = '4px';
      cursorEl.style.fontSize = '12px';
      cursorEl.innerText = 'Click on the pdf page to drop the element';
      viewerEl.appendChild(cursorEl);
      this.cursorEl = cursorEl;

      const showTextWithCursor: EventListener = (event) => {
        const mouseEvent = event as MouseEvent;
        const rect = viewerEl.getBoundingClientRect();
        cursorEl.style.left = `${mouseEvent.clientX - rect.left + 10}px`;
        cursorEl.style.top = `${mouseEvent.clientY - rect.top + 10}px`;
      };
      viewerEl.addEventListener('mousemove', showTextWithCursor);
      this.activeClickHandler = showTextWithCursor;
    }

    
    const pages = viewerEl.getElementsByClassName('page');
    if (pages.length) {
      for (let i = 0; i < pages.length; i++) {
        const textLayers = pages[i].getElementsByClassName('textLayer');
        for (let j = 0; j < textLayers.length; j++) {
          const textLayer = textLayers[j] as HTMLElement;

          if (!this.isMobileView) {
            const onClick = (event: MouseEvent) => {
              if (this.activeClickHandler) viewerEl.removeEventListener('mousemove', this.activeClickHandler);
              if (this.cursorEl) this.cursorEl.remove();
              this.generateSignTag(event, textLayer, type, dimensions);
            };
            textLayer.addEventListener('click', onClick, { once: true });
            this.lastClickListeners.push(() => {
              textLayer.removeEventListener('click', onClick);
            });
          } else {
            const onTouch = (event: TouchEvent) => {
              const touch = event.changedTouches[0];
              if (!touch) return;
              event.preventDefault();
              this.generateSignTag(
                { clientX: touch.clientX, clientY: touch.clientY } as MouseEvent,
                textLayer,
                type,
                dimensions
              );
            };
            const onPointer = (event: PointerEvent) => {
              event.preventDefault();
              this.generateSignTag(
                { clientX: event.clientX, clientY: event.clientY } as MouseEvent,
                textLayer,
                type,
                dimensions
              );
            };
            if (window.PointerEvent) {
              textLayer.addEventListener('pointerdown', onPointer, { once: true });
              this.lastClickListeners.push(() => {
                textLayer.removeEventListener('pointerdown', onPointer);
              });
            } else {
              textLayer.addEventListener('touchstart', onTouch, { once: true, passive: false });
              this.lastClickListeners.push(() => {
                textLayer.removeEventListener('touchstart', onTouch);
              });
            }
          }
        }
      }
    }
  }

  generateSignTag(event: MouseEvent, textLayer: HTMLElement, type: EnumSignTagTypes, dimensions: { height: string; width: string }) {
    const rect = textLayer.getBoundingClientRect();
    const element = document.createElement('span');
    element.style.position = 'absolute';
    element.style.width = dimensions.width;
    element.style.height = dimensions.height;
    element.style.left = (event.clientX - rect.left) + 'px';
    element.style.top = (event.clientY - rect.top) + 'px';
    element.className = 'sign-tag-element-container';
    element.setAttribute('data-tag-type', type);
    textLayer.appendChild(element);

    const onRemoveElement = () => {
      element.remove();
    }

    this.signService.renderTagElement(element, type === EnumSignTagTypes.CHECKBOX ? '' : type, type, false, onRemoveElement);
  }
}